// Fallback translation system that works without i18next
import React from 'react';
import { formatDate as formatDateUtil, formatTime as formatTimeUtil, formatDateLong as formatDateLongUtil } from './dateUtils';

const translations = {
  en: {
    common: {
      add: "Add",
      edit: "Edit",
      delete: "Delete",
      save: "Save",
      saving: "Saving...",
      cancel: "Cancel",
      clear: "Clear",
      back: "Back",
      search: "Search",
      actions: "Actions",
      view: "View",
      loading: "Loading...",
      noData: "No data found",
      total: "Total",
      date: "Date",
      type: "Type",
      status: "Status",
      name: "Name",
      phone: "Phone",
      address: "Address",
      description: "Description",
      amount: "Amount",
      price: "Price",
      show: "Show",
      cost: "Cost",
      active: "Active",
      inactive: "Inactive",
      lightMode: "Light Mode",
      darkMode: "Dark Mode",
      deletedSuccess: "deleted successfully",
      all: "All",
      remove: "Remove",
      items: "Items",
      noRecordsFound: "No records found",
      prev: "Prev",
      next: "Next",
      print: "Print",
      printSize: "Print size",
      retry: "Retry",
      notAvailable: "N/A",
      unknownError: "Unknown error",
      note: "Note:",
      profit: "Profit",
      calculation: "Calculation:",
      payment: "Payment",
      remainingLabel: "Remaining",
      confirm: "Confirm",
      notes: "Notes",
      notesPlaceholder: "Additional notes (optional)",
      select: "Select",
      close: "Close",
      yes: "Yes",
      no: "No",
      apply: "Apply"
    },
    nav: {
      dashboard: "Dashboard",
      orders: "Orders",
      customers: "Customers",
      inventory: "Inventory",
      purchases: "Purchases",
      suppliers: "Suppliers",
      employees: "Employees",
      expenses: "Expenses",
      rozNamcha: "Roz Namcha",
      rent: "Rent",
      printingPress: "Printing Press",
      reports: "Reports",
      bank: "Bank",
      recordLookup: "Record Lookup",
      sales: "Sales",
      quotations: "Quotations",
      settings: "Settings",
      backup: "Backup",
      userManagement: "User Management",
      logout: "Logout"
    },
    printing: {
      title: "Printing Press",
      subtitle: "Manage flag printing jobs and payments",
      selectPrinter: "Select Printer",
      newPrinter: "Create Printer",
      printer: "Printer",
      printerName: "Printer Name",
      printerPhone: "Printer Phone",
      printerAddress: "Printer Address",
      jobTitle: "Printing Job",
      flagName: "Flag Name",
      size: "Size",
      qty: "Qty",
      totalMeters: "Total Size (Meter)",
      perMeterPrice: "Price per Meter",
      totalPrice: "Total Price",
      makingUnitPrice: "Making price (unit)",
      sellingUnitPrice: "Selling price (unit)",
      lineSubtotal: "Subtotal",
      grandTotal: "Grand total",
      totalProfit: "Total profit",
      makingRateColumn: "Making / rate",
      afnPerMeter: "AFN/m",
      cash: "Cash",
      partial: "Partial",
      credit: "Credit",
      paymentAmount: "Payment Amount",
      reference: "Reference",
      addLine: "Add Line",
      createRecord: "Create Printing Record",
      records: "Printing Records",
      billTitle: "Printing Bill",
      billNumber: "Bill No.",
      printBill: "Print Bill",
      viewBill: "View Bill",
      payments: "Payments",
      paymentExceedsDue: "Payment exceeds remaining balance",
      paymentSectionTitle: "Payment",
      paymentMethodLabel: "Payment Method",
      paymentHintCash: "Pay full amount now",
      paymentHintPartial: "Pay partial amount now, rest later",
      paymentHintCredit: "No payment now, pay later",
      paymentAmountPlaceholder: "Enter amount to pay now",
      amountPayingNow: "Amount paying now",
      remainingAfterPayment: "Remaining after payment",
      partialPaymentRequired: "Enter a partial payment amount greater than zero",
      partialPaymentExceedsTotal: "Partial payment cannot exceed the total",
      lineItemsHeading: "Line items",
      lineProfit: "Line profit",
      perMeterPriceLegacy: "Per meter (legacy)",
      editPaymentsOnDetails: "Manage payments on the payments page after saving.",
      created: "Printing record created successfully",
      failedSave: "Failed to save printing record",
      pressBillLinesTruncated: "Showing first {{shown}} of {{total}} lines on this bill. Footer totals are for the full job.",
      lineNo: "No."
    },
    rent: {
      title: "Rent",
      subtitle: "Manage shops and rent payments",
      shop: "Shop",
      shopNo: "Shop No",
      tenant: "Tenant",
      addShop: "Create Shop Rent",
      editShop: "Edit Shop Rent",
      rentDate: "Rent Date",
      duration: "Duration",
      periodType: "Period Type",
      weekly: "Weekly",
      monthly: "Monthly",
      rentAmount: "Rent Amount",
      totalPaid: "Total Paid",
      totalDue: "Total Due",
      remainingPeriods: "Remaining Periods",
      addPayment: "Add Payment",
      amountPaid: "Amount Paid",
      paymentDate: "Payment Date",
      ownerName: "Property owner (landlord)",
      ownerNamePlaceholder: "Name of person or company you pay rent to",
      searchPlaceholder: "Search shop, tenant, owner…",
      filterPeriod: "Period",
      allPeriods: "All periods",
      deleteShop: "Delete rent record",
      deleteConfirm: "Delete this shop rent record? Payments will be removed too.",
      backToList: "Back to list",
      loadError: "Could not load rent records.",
      deleteError: "Could not delete this record.",
      paymentAdded: "Payment saved.",
      paymentAddError: "Could not save payment.",
      shopDeleted: "Rent record removed.",
      printBill: "Print Rent Bill",
      printSlip: "Print Thermal Slip",
      billTitle: "Rent Bill",
      thermalSlipTitle: "Rent Payment Slip",
      paymentHistory: "Payment History",
      editPayment: "Edit Payment",
      deletePaymentConfirm: "Delete this payment record?",
      paymentUpdated: "Payment updated.",
      paymentUpdateError: "Could not update payment.",
      paymentDeleted: "Payment deleted.",
      paymentDeleteError: "Could not delete payment.",
      thankYou: "Thank you"
    },
    quotations: {
      title: "Quotations",
      subtitle: "Manage price quotations",
      createQuotation: "Create Quotation",
      editQuotation: "Edit Quotation",
      newQuotation: "Create Quotation",
      customer: "Customer",
      searchCustomer: "Search customer...",
      selectExisting: "Select Existing",
      addNewCustomer: "Add New Customer",
      enterCustomerName: "Enter customer name...",
      items: "Items",
      item: "Item",
      itemName: "Item Name",
      searchItem: "Search item...",
      noItemsMatchSearch: "No items match your search.",
      itemSelected: "Selected: {{name}}",
      clearItemSearch: "Clear",
      mode: "Mode",
      switchToSelect: "Switch to Select",
      switchToManual: "Switch to Manual",
      flagSize: "Flag Size",
      flagSizePlaceholder: "e.g., 3x5 feet",
      designType: "Design Type",
      designTypePlaceholder: "e.g., Embroidered",
      quantity: "Quantity",
      priceEstimate: "Price Estimate (AFN)",
      purchasePrice: "Purchase Price (AFN)",
      profitPercent: "Profit (%)",
      profitPercen: "Profit (%)",
      salePriceWithProfit: "Sale Price (with profit)",
      purchaseSubtotal: "Purchase Subtotal",
      saleSubtotal: "Sale Subtotal",
      subtotal: "Subtotal",
      addItem: "+ Add Item",
      remove: "Remove",
      notes: "Notes",
      create: "Create",
      update: "Update",
      quotationCreated: "Quotation created successfully!",
      quotationUpdated: "Quotation updated successfully!",
      failedToLoad: "Failed to load quotation",
      quotationNotFound: "Quotation not found",
      backToQuotations: "Back to Quotations",
      searchByCustomer: "Search by customer or ID...",
      id: "ID",
      date: "Date",
      amount: "Amount",
      actions: "Actions",
      noQuotationsFound: "No quotations found",
      confirmDelete: "Confirm Delete",
      deleteMessage: "Are you sure you want to delete quotation for",
      yesDelete: "Yes, Delete",
      createNewQuotation: "Create a new price quotation",
      updateQuotationDetails: "Update quotation details",
      printHeading: "Quotation",
      printQuotation: "Print Quotation",
      billNoLabel: "Bill No.",
      linesTruncated: "Showing first {{shown}} of {{total}} items on this page.",
      quotationDateLabel: "Quotation date",
      quotationDateHelp: "Optional — defaults to today if empty"
    },
    bank: {
      title: "Bank",
      subtitle: "Manage deposits and withdrawals",
      currentBalance: "Current Balance",
      totalDeposits: "Total Deposits",
      totalWithdraws: "Total Withdrawals",
      availableIncome: "Available Income to Deposit",
      newTransaction: "New Transaction",
      depositIncome: "Deposit Income",
      deposit: "Deposit",
      withdraw: "Withdraw",
      credit: "Credit",
      debit: "Debit",
      manual: "Manual",
      income: "Income",
      amount: "Amount",
      type: "Type",
      source: "Source",
      note: "Note",
      transactions: "Transactions",
      noTransactions: "No transactions found",
      failedToLoad: "Failed to load bank data",
      amountRequired: "Amount must be greater than zero",
      failedToSave: "Failed to save transaction",
      depositSuccess: "Deposit recorded successfully",
      withdrawSuccess: "Withdrawal recorded successfully",
      updateSuccess: "Transaction updated successfully",
      deleteSuccess: "Transaction deleted successfully",
      failedToDelete: "Failed to delete transaction",
      incomeDepositSuccess: "Income deposited successfully",
      noIncomeAvailable: "No income available for deposit"
    },
    settings: {
      title: "System Settings",
      subtitle: "Configure system preferences and options",
      systemIdentity: "System Identity",
      systemName: "System Name",
      systemLogo: "System Logo URL",
      currencySettings: "Currency Settings",
      primaryCurrency: "Primary Currency",
      currencySymbol: "Currency Symbol",
      regionalSettings: "Regional Settings",
      dateFormat: "Date Format",
      timeFormat: "Time Format",
      timezone: "Timezone",
      businessSettings: "Business Settings",
      taxRate: "Tax Rate (%)",
      lowStockThreshold: "Low Stock Threshold",
      contactInfo: "Contact Information",
      companyAddress: "Company Address",
      companyPhone: "Company Phone",
      companyEmail: "Company Email",
      saveSettings: "Save Settings",
      settingsSaved: "Settings saved successfully",
      failedToSave: "Failed to save settings",
      failedToLoad: "Failed to load settings",
      backupSection: "Data backup",
      backupIntro:
        "Export all database tables to Excel (one sheet per model, including m2m_* columns for many-to-many links and file paths for uploads) or a full SQLite SQL text dump. The ZIP option also includes your media/uploads folder. Automatic backups use your selections below and are stored on the server.",
      backupAutomatic: "Enable automatic backups",
      backupFrequency: "Automatic backup interval",
      backupDaily: "Daily",
      backupWeekly: "Weekly",
      backupMonthly: "Monthly (approx. 30 days)",
      backupYearly: "Yearly (approx. 365 days)",
      backupIncludeExcel: "Include Excel in automatic backups",
      backupIncludeSql: "Include SQL in automatic backups",
      backupLastAuto: "Last automatic backup",
      backupManualHint:
        "Manual download runs immediately in your browser (does not depend on the schedule above).",
      backupExcel: "Download Excel",
      backupSql: "Download SQL",
      backupBoth: "Download both (ZIP)",
      backupDownloadStarted: "Backup download started",
      backupDownloadFailed: "Backup download failed",
      backupAutoLocation:
        "Automatic files are saved on the server under media/backups/auto (ZIP when both types are enabled).",
      backupAuthLoading: "Checking your account…",
      backupNeedsLogin: "Log in to enable backups and downloads.",
      backupNeedsAdmin:
        "Backups are limited to administrators (role \"admin\") or superusers. Ask an admin to set your role, or use a Django superuser account.",
      backupJwtVsAdminNote:
        "Django admin (/admin/) only sets a browser session for that site. This app uses JWT tokens: open the app Login page and sign in with the same username and password as your superuser. (Leaving \"Remember me\" unchecked keeps tokens in this browser tab only — the app now reads those correctly.)",
      backupNeedJwtLogin:
        "Sign in on this app’s Login screen with your Django username and password (superuser is fine). Opening Django admin alone does not send a token to this app.",
      backupPageTitle: "Backup & restore",
      backupPageSubtitle: "Create full-system backups by period and restore from a previous backup.",
      backupOpenPage: "Open backup page",
      backupFullSection: "Full system backup",
      backupRangeSection: "Backup by period",
      backupCreateTitle: "Create backup",
      backupRestoreSection: "Restore backup",
      backupRestoreIntro: "Restore a previous full backup ZIP or SQL file. This replaces current database data.",
      backupRestoreWarning: "Restore overwrites current system data. Use only trusted full backup files.",
      backupRangeIntro:
        "Choose daily, weekly, monthly, yearly, or a custom label. Every download is a full backup of all system data (all modules), not a filtered subset.",
      backupAlwaysFullBadge: "Always full system data",
      backupFullDataNote:
        "Period only labels the file name (e.g. daily / weekly). Excel, SQL, and ZIP always include the complete database. ZIP also includes uploaded media.",
      backupCustomLabelNote:
        "Custom dates are stored in the file name only. The backup still contains all system data.",
      backupPreset: "Backup period",
      backupPresetAll: "All data",
      backupCustomRange: "Custom range",
      backupDateFrom: "From date",
      backupDateTo: "To date",
      backupRestoreChooseFile: "Choose backup file",
      backupRestoreButton: "Restore backup",
      backupRestoring: "Restoring backup...",
      backupRestoreSuccess: "Backup restored successfully",
      backupRestoreFailed: "Backup restore failed",
      backupRangeDownloadExcel: "Download Excel",
      backupRangeDownloadZip: "Download ZIP",
      backupFilteredZipNote:
        "ZIP includes Excel + SQL + media. Period filters do not reduce the data — they only label the backup.",
      backupCustomDateRequired: "Select both start and end dates for custom range.",
      backupExcelDesc: "All tables in one Excel workbook",
      backupSqlDesc: "Full SQLite SQL dump for restore",
      backupBothDesc: "Excel + SQL + media uploads (recommended)",
      backupRecommended: "Best",
      backupDownloadAction: "Download",    },
    userManagement: {
      title: "User Management",
      subtitle: "Manage system users and permissions",
      addUser: "Add User",
      editUser: "Edit User",
      username: "Username",
      email: "Email",
      firstName: "First Name",
      lastName: "Last Name",
      role: "Role",
      admin: "Admin",
      staff: "Staff",
      password: "Password",
      confirmPassword: "Confirm Password",
      isActive: "Active",
      createdAt: "Created At",
      actions: "Actions",
      edit: "Edit",
      delete: "Delete",
      cancel: "Cancel",
      save: "Save",
      deleteConfirm: "Are you sure you want to delete this user?",
      userAdded: "User added successfully",
      userUpdated: "User updated successfully",
      userDeleted: "User deleted successfully",
      failedToAdd: "Failed to add user",
      failedToUpdate: "Failed to update user",
      failedToDelete: "Failed to delete user",
      failedToLoad: "Failed to load users",
      passwordMismatch: "Passwords do not match",
      fullAccess: "Full system access",
      partialAccess: "Limited access based on role"
    },
    rozNamcha: {
      title: "Roz Namcha",
      addEntry: "Add Roz Namcha Entry",
      itemName: "Item Name",
      costPrice: "Cost/Price",
      dailyTotal: "Daily Total",
      weeklyTotal: "Weekly Total",
      monthlyTotal: "Monthly Total",
      today: "Today",
      thisWeek: "This Week",
      thisMonth: "This Month",
      noEntriesFound: "No entries found",
      dailyReport: "Daily Roz Namcha Report",
      weeklyReport: "Weekly Roz Namcha Report",
      monthlyReport: "Monthly Roz Namcha Report",
      totalEntries: "Total Entries",
      credit: "Credit",
      debit: "Debit",
      balance: "Balance",
      failedToFetch: "Failed to fetch entry",
      itemNameRequired: "Item name is required",
      dateRequired: "Date is required",
      costPriceRequired: "Cost/Price must be greater than zero",
      fixErrors: "Please fix the errors in the form",
      entryUpdated: "Entry updated successfully",
      entryCreated: "Entry created successfully",
      failedToSave: "Failed to save entry",
      entryNotFound: "Entry not found",
      entryDeleted: "Entry deleted successfully",
      failedToDelete: "Failed to delete entry",
      addNew: "Add Roz Namcha Entry",
      editEntry: "Edit Roz Namcha Entry",
      enterItemName: "Enter item name",
      enterDescription: "Enter description (optional)",
      saving: "Saving...",
      updateEntry: "Update Entry",
      saveEntry: "Save Entry"
    },
    modals: {
      confirmDelete: "Confirm Delete",
      deleteMessage: "Are you sure you want to delete",
      yesDelete: "Yes, Delete",
      keep: "Keep",
      confirm: "Confirm",
      cancel: "Cancel"
    },
    pagination: {
      showing: "Showing",
      to: "to",
      of: "of",
      results: "results",
      perPage: "per page",
      page: "Page",
      previous: "Previous",
      next: "Next"
    },
    reports: {
      startDate: "Start Date",
      endDate: "End Date"
    },
    dashboard: {
      title: "Dashboard",
      subtitle: "Real-time business overview and analytics",
      refresh: "Refresh",
      failedToLoad: "Failed to load dashboard data",
      retry: "Retry",
      filterLabel: "Filter:",
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
      yearly: "Yearly",
      dailyRevenue: "Daily Revenue",
      weeklyRevenue: "Weekly Revenue",
      monthlyRevenue: "Monthly Revenue",
      dailyExpenses: "Daily Expenses",
      weeklyExpenses: "Weekly Expenses",
      monthlyExpenses: "Monthly Expenses",
      netProfit: "Net Profit",
      netBalance: "Net Balance",
      totalOrders: "Total Orders",
      customerDues: "Customer Dues",
      supplierPayables: "Supplier Payables",
      lowStockItems: "Low Stock Items",
      vsLastMonth: "vs last month",
      thisMonth: "This month",
      today: "Today",
      thisWeek: "This week",
      positive: "Positive",
      negative: "Negative",
      pending: "pending",
      critical: "critical",
      fromCustomers: "From {{count}} customers",
      toSuppliers: "To {{count}} suppliers",
      revenueVsExpensesTrend: "Revenue vs Expenses Trend",
      financialOverview: "Financial Overview",
      noTrendData: "No trend data available",
      grossProfit: "Gross Profit",
      realTimeTransactions: "Real-time Transactions",
      lastUpdated: "Last updated",
      type: "Type",
      details: "Details",
      amount: "Amount",
      date: "Date",
      status: "Status",
      noRecentTransactions: "No recent transactions",
      showing: "Showing",
      to: "to",
      of: "of",
      page: "Page",
      customersWithDues: "Customers with Outstanding Dues",
      customer: "Customer",
      dueAmount: "Due Amount",
      orders: "Orders",
      noOutstandingDues: "No outstanding dues",
      suppliersWithBalance: "Suppliers with Outstanding Balance",
      supplier: "Supplier",
      balance: "Balance",
      purchases: "Purchases",
      noOutstandingBalances: "No outstanding balances",
      totalCustomers: "Total Customers",
      active: "Active",
      totalInventoryValue: "Total Inventory Value",
      itemsInStock: "{{count}} items in stock",
      activeEmployees: "Active Employees",
      monthlySalary: "Monthly Salary",
      order: "Order",
      purchase: "Purchase",
      expense: "Expense",
      roznamcha: "Roz Namcha",
      completed: "Completed",
      pending: "Pending",
      cancelled: "Cancelled",
      partial: "Partial",
      due: "Due",
      revenue: "Revenue",
      expenses: "Expenses",
      supplierPayment: "Supplier Payment",
      chartNoData: "No data",
      pieRevenue: "Revenue",
      pieExpenses: "Expenses",
      piePurchases: "Purchases",
      orderChartLabel: "Order {{id}}",
      statusPaid: "Paid",
      stockAvailable: "Stock available",
      insufficientStock: "Insufficient stock",
      currentStock: "Current stock",
      requested: "Requested",
      shortage: "Shortage",
      customRange: "Custom Range",
      dateFrom: "From",
      dateTo: "To",
      apply: "Apply"
    },
    sales: {
      title: "Sales",
      subtitle: "Create new sale and process payment",
      viewOrders: "View Orders",
      saleCompleted: "Sale Completed!",
      printBill: "Print Bill",
      newSale: "Create Sale",
      ordersCreated: "Orders Created",
      customer: "Customer",
      totalAmount: "Total Amount",
      status: "Status",
      completed: "Completed",
      customerInformation: "Customer Information",
      searchCustomer: "Search Customer *",
      searchExistingCustomer: "Search existing customer",
      searchCustomerPlaceholder: "Type to search customer...",
      enterCustomerManually: "Or enter manually (will create new customer if not exists)",
      typeCustomerName: "Type customer name...",
      createCustomerWithDetails: "Create New Customer with Details",
      searchPlaceholder: "Search by name or phone...",
      noCustomersFound: "No customers found",
      name: "Name",
      phone: "Phone",
      email: "Email",
      address: "Address",
      saleItems: "Sale Items",
      addItem: "Add Item",
      item: "Item *",
      selectItem: "Select item...",
      quantity: "Quantity *",
      qty: "Qty",
      pricePerUnit: "Price/Unit (AFN) *",
      totalAfn: "Total (AFN)",
      qualityDesignType: "Quality/Design Type",
      selectDesignType: "Select design type (optional)",
      addNewDesignType: "Add new design type",
      newDesignTypeName: "New design type name",
      stockSource: "Stock Source *",
      pressStock: "Press Stock",
      homeStock: "Home Stock",
      flagSizeExample: "e.g., 3x5 ft",
      qualityDesignExample: "e.g., Premium, Standard",
      available: "Available",
      orderSummary: "Order Summary",
      items: "Items",
      subtotal: "Subtotal",
      total: "Total",
      proceedToPayment: "Proceed to Payment",
      resetForm: "Reset Form",
      processing: "Processing...",
      financial: "Financial Details",
      discount: "Discount",
      netAmount: "Net Amount",
      notes: "Notes",
      additionalNotesPlaceholder: "Additional notes...",
      createSale: "Create Sale",
      confirmSale: "Confirm Sale",
      selectCustomer: "Select customer",
      quickInfo: "Quick Info",
      totalCustomers: "Total Customers",
      availableItems: "Available Items",
      itemsInCart: "Items in Cart",
      pleaseSelectCustomer: "Please select a customer",
      pleaseSelectItem: "Please select an item",
      quantityRequired: "Quantity must be greater than 0",
      insufficientStock: "Insufficient stock. Available: {{count}}",
      priceRequired: "Price must be greater than 0",
      fixErrors: "Please fix the errors before proceeding",
      atLeastOneItem: "At least one item is required",
      saleCompletedSuccess: "Sale completed successfully!",
      failedToFetchCustomers: "Failed to fetch customers",
      failedToFetchItems: "Failed to fetch inventory items",
      designTypeAdded: "Design type added successfully",
      failedToAddDesignType: "Failed to add design type",

      // Direct sales (no stock) feature
      directSalesTitle: "Direct Sales",
      directSalesSubtitle: "Items bought externally & sold directly",
      newDirectSale: "New Direct Sale",
      noDirectSalesFound: "No direct sales found",
      createFirstDirectSale: "Create First Direct Sale",
      unpaid: "Unpaid",

      createDirectSaleTitle: "Create Direct Sale",
      createDirectSaleSubtitle: "Buy from external source & sell directly (No stock)",
      itemsToSell: "Items to Sell",
      customerNameRequiredLabel: "Customer Name *",
      selectCustomerOptional: "Select from list (optional)",
      selectCustomerPlaceholder: "Select customer...",

      sellingPricePerUnit: "Selling Price/Unit *",
      costPricePerUnit: "Cost Price/Unit",
      supplierNameOptionalLabel: "Supplier Name (Optional)",

      financialSummary: "Financial Summary",
      subtotalSelling: "Subtotal (Selling)",
      totalCostBuying: "Total Cost (Buying)",
      profit: "Profit",

      createDirectSaleDraft: "Create Direct Sale (Draft)",
      confirmDirectSale: "Confirm Direct Sale",

      directSaleDetailsTitle: "Direct Sale",
      saleInformation: "Sale Information",
      paymentStatus: "Payment Status",
      saleDate: "Date",
      itemsSectionTitle: "Items",
      notesSectionTitle: "Notes",
      paymentsSectionTitle: "Payments",
      addPayment: "Add Payment",
      saleNotFound: "Sale not found",
      backToDirectSales: "Back to Direct Sales",
      amount: "Amount",
      paymentMethodLabel: "Payment Method",
      addPaymentButton: "Add Payment",
      failedToLoadDirectSales: "Failed to load direct sales",
      failedToLoadDirectSaleDetails: "Failed to load sale details",
      paymentAddedSuccess: "Payment added successfully",
      failedToAddPayment: "Failed to add payment",

      // Direct Sales
      directSale: "Direct Sale",
      saleInformation: "Sale Information",
      financialSummary: "Financial Summary",
      items: "Items",
      notes: "Notes",
      payments: "Payments",
      addPayment: "Add Payment",
      print: "Print",
      printSize: "Print size",
      editAndAddItems: "Edit & Add Items",
      saleNotFound: "Sale not found",
      backToDirectSales: "Back to Direct Sales",
      size: "Size",
      design: "Design",
      supplier: "Supplier",
      cash: "Cash",
      card: "Card",
      bankTransfer: "Bank Transfer",
      check: "Check",
      printDirectSaleBill: "Print Direct Sale Bill"
    },
    directSales: {
      title: "Direct Sales",
      subtitle: "Manage direct sales without inventory",
      directSale: "Direct Sale",
      saleInformation: "Sale Information",
      financialSummary: "Financial Summary",
      totalAmount: "Total Amount",
      cost: "Cost",
      discount: "Discount",
      netAmount: "Net Amount",
      profit: "Profit",
      items: "Items",
      notes: "Notes",
      payments: "Payments",
      addPayment: "Add Payment",
      print: "Print",
      printSize: "Print size",
      editAndAddItems: "Edit & Add Items",
      saleNotFound: "Sale not found",
      backToDirectSales: "Back to Direct Sales",
      status: "Status",
      paymentStatus: "Payment Status",
      date: "Date",
      size: "Size",
      design: "Design",
      supplier: "Supplier",
      qty: "Qty",
      price: "Price",
      total: "Total",
      amount: "Amount",
      paymentMethod: "Payment Method",
      paymentAdded: "Payment added successfully",
      failedToAddPayment: "Failed to add payment",
      failedToLoad: "Failed to load sale details",
      cash: "Cash",
      card: "Card",
      bankTransfer: "Bank Transfer",
      check: "Check",
      printDirectSaleBill: "Print Direct Sale Bill",
      cancel: "Cancel",
      deletedSuccess: "Direct sale deleted successfully",
      deleteFailed: "Could not delete direct sale",
      deleteDirectSaleTitle: "Delete direct sale?",
      deleteDirectSaleMessage: "This permanently removes this direct sale and its payments."
    },
    auth: {
      welcomeBack: "Welcome Back",
      signInSubtitle: "Sign in to your dashboard (Test: admin/admin)",
      emailUsername: "Email or Username",
      emailUsernamePlaceholder: "Enter your email or username",
      password: "Password",
      passwordPlaceholder: "Enter your password",
      rememberMe: "Remember me",
      forgotPassword: "Forgot password?",
      signIn: "Sign in",
      signingIn: "Signing in...",
      createAccount: "Create account",
      noAccount: "Don't have an account?",
      createAccountTitle: "Create Account",
      joinSystem: "Join our system",
      username: "Username",
      usernamePlaceholder: "Choose a username",
      emailAddress: "Email Address",
      emailPlaceholder: "Enter your email address",
      confirmPassword: "Confirm Password",
      confirmPasswordPlaceholder: "Confirm your password",
      acceptTerms: "I accept the",
      termsAndConditions: "Terms and Conditions",
      and: "and",
      privacyPolicy: "Privacy Policy",
      creatingAccount: "Creating account...",
      alreadyHaveAccount: "Already have an account?",
      signInHere: "Sign in here",
      emailRequired: "Email/Username is required",
      passwordRequired: "Password is required",
      loginFailed: "Login failed. Please try again.",
      usernameRequired: "Username is required",
      usernameMinLength: "Username must be at least 3 characters",
      usernameInvalid: "Username can only contain letters, numbers, and underscores",
      emailInvalid: "Please enter a valid email address",
      passwordMinLength: "Password must be at least 8 characters",
      confirmPasswordRequired: "Please confirm your password",
      passwordsDontMatch: "Passwords do not match",
      fixValidationErrors: "Please fix the validation errors",
      acceptTermsRequired: "You must accept the terms and conditions",
      chooseStrongerPassword: "Please choose a stronger password",
      registrationFailed: "Registration failed. Please try again.",
      weak: "Weak",
      medium: "Medium",
      strong: "Strong",
      atLeast8Chars: "At least 8 characters",
      lowercaseLetter: "Lowercase letter",
      uppercaseLetter: "Uppercase letter",
      number: "Number",
      specialChar: "Special character"
    },
    orders: {
      title: "Orders",
      subtitle: "Customer order request - stock not deducted until converted to sale",
      newOrder: "Create New Order",
      filters: "Filters",
      status: "Status",
      allStatus: "All Status",
      customer: "Customer",
      searchCustomer: "Search customer...",
      startDate: "Start Date",
      endDate: "End Date",
      bulkActions: "order(s) selected",
      cancelSelected: "Cancel Selected",
      orderId: "Order ID",
      flagSize: "Flag Size",
      flagStandTypeLabel: "Flag Stand Type",
      qualityDesignLabel: "Quality/Design Type",
      qty: "Qty",
      total: "Total",
      due: "Due",
      actions: "Actions",
      itemsPerPage: "Items per page",
      previous: "Previous",
      next: "Next",
      page: "Page",
      exportCsv: "Export CSV",
      noOrdersFound: "No orders found",
      cancelOrder: "Cancel Order",
      completeOrder: "Complete Order",
      cancelMultiple: "Cancel Multiple Orders",
      cancelConfirm: "Are you sure you want to cancel this order?",
      completeConfirm: "Mark this order as complete?",
      cancelSelectedConfirm: "Cancel selected orders?",
      cancelPaymentConfirm: "Remove this payment from the order? The order balance will be updated.",
      cancelOrders: "Cancel Order(s)",
      keep: "Keep",
      orderCancelled: "Order cancelled successfully",
      orderCompleted: "Order marked as complete",
      ordersCancelled: "orders cancelled successfully",
      createOrderPageTitle: "Create New Order",
      createOrderPageSubtitle: "Customer order request - stock not deducted until converted to sale",
      addItemButton: "Add Item",
      convertToSale: "Convert to Sale",
      markReady: "Mark as Ready",
      markInProduction: "Mark In Production",
      inProduction: "In Production",
      ready: "Ready",
      delivered: "Delivered",
      partiallyDelivered: "Partially delivered",
      editOrderLoadFailed: "Failed to load order for editing.",

      productType: "Product Type",
      stockSource: "Stock Source *",
      enterQuantity: "Enter quantity",
      enterPrice: "Enter price",
      createOrderButton: "Create Order",
      creatingOrder: "Creating Order...",
      headerTotal: "Total",
      manualEntry: "Manual entry",
      customerRequired: "Customer *",
      supplierRequired: "Supplier *",
      supplier: "Supplier",
      searchSupplierPlaceholder: "Search supplier by name or phone...",
      noSuppliersInSearch: "No suppliers found",
      weOwe: "We owe",
      supplierOutstandingHint: "Outstanding on supplier ledger (we owe)",
      supplierOffsetHint: "Use “Settle from supplier ledger” to deduct this sale from that balance.",
      supplierOffsetPayNote: "This payment reduces the supplier outstanding balance (what we owe them).",
      searchCustomerPlaceholder: "Search or type customer name...",
      noCustomersInSearch: "No customers found",
      fetchNoCustomers: "No customers found. Add customers first.",
      fetchCustomersFailed: "Failed to load customers: {{message}}",
      fetchItemsFailed: "Failed to load items: {{message}}",
      orderItemsSection: "Order lines",
      itemNumber: "Item {{n}}",
      notSelected: "Not selected",
      qtyAbbr: "Qty",
      removeItem: "Remove",
      manualItemHint: "Manual entry — stock is not deducted",
      itemNameManual: "Item name *",
      itemNameManualPh: "Enter item name manually...",
      loadingItems: "Loading items...",
      noItemsUseManual: "No items available. Use manual entry to add without stock.",
      selectItem: "Select an item...",
      itemOptionStock: "{{name}} ({{sku}}) — press: {{press}} | home: {{home}}",
      inventoryFilteredFallback:
        "Showing all finished products — add a “Flag Stand” category in inventory or put “stand” in the item name/category to narrow this list.",
      productTypeLabel: "Product type *",
      productFlag: "Flag",
      productFlagStand: "Flag stand",
      qualityDesignPh: "e.g. Premium, Standard",
      availableStock: "Available: {{count}}",
      purchasePrice: "Purchase price",
      costPricePh: "Cost price",
      sellingPrice: "Selling price *",
      sellingPricePh: "Selling price",
      lineTotal: "Total:",
      checkingInventory: "Checking inventory...",
      totalAmountLabel: "Total amount:",
      totalProfitLabel: "Total profit:",
      cancelButton: "Cancel",
      customerDetailsTitle: "Customer details",
      notesLabel: "Notes:",
      orderSummaryTitle: "Order summary",
      pricePerUnitLabel: "Price per unit:",
      paymentModalTitle: "Payment information",
      payLaterNote: "You can enter 0 to save the order now and record payment when the customer pays.",
      fullPayment: "Full payment",
      partialPaymentRadio: "Partial payment",
      paymentAmountLabel: "Payment amount *",
      remainingPayment: "Remaining: AFN {{amount}}",
      paymentMethodLabel: "Payment method *",
      payMethodCash: "Cash",
      payMethodCredit: "Credit",
      payMethodPartial: "Partial",
      payMethodSupplierOffset: "Settle from supplier ledger",
      creating: "Creating...",
      errCustomerRequired: "Customer is required",
      errCustomerName: "Customer name is required",
      errCustomerPhone: "Customer phone is required",
      errAtLeastOneItem: "At least one item is required",
      errItemSelect: "Item selection is required",
      errManualName: "Item name is required",
      errFlagSize: "Flag size is required",
      errFlagStand: "Flag stand type is required",
      errQuantityPositive: "Quantity must be greater than 0",
      errSellingPrice: "Selling price must be greater than 0",
      errPaymentPositive: "Payment amount must be greater than 0",
      errPaymentNegative: "Payment amount cannot be negative",
      errPaymentExceeds: "Payment cannot exceed total amount",
      errCreateCustomer: "Failed to create customer",
      recordOrderNoPayment: "Save order with no payment",
      stateCreatedMessage: "Order created. Bill is ready to print.",
      editOrderPageTitle: "Edit Order #{{id}}",
      editOrderPageSubtitle: "Update order details",
      selectStatus: "Select status...",
      statusPending: "Pending",
      statusCompleted: "Completed",
      statusCancelled: "Cancelled",
      updateOrderButton: "Update Order",
      updatingOrder: "Updating...",
      errStatusRequired: "Status is required",
      stateUpdatedMessage: "Order updated successfully!",
      billDateNotSet: "—",
      billNotesHeading: "Notes",
      returnRefund: "Return/Refund",
      returnType: "Return Type",
      fullReturn: "Full Return",
      partialReturn: "Partial Return",
      returnReasonPlaceholder: "Enter reason for return/refund...",
      processReturnRefund: "Process Return/Refund"
    },
    sales: {
      title: "Sales",
      subtitle: "Process sales and deduct stock",
      printBill: "Print Bill",
      printQuotation: "Print Quotation",
      newSale: "Create Sale",
      createSale: "Create Sale",
      confirmSale: "Confirm Sale",
      saleId: "Sale ID",
      saleDate: "Sale Date",
      customer: "Customer",
      customerName: "Customer Name",
      searchCustomer: "Search Customer",
      searchExistingCustomer: "Search existing customer",
      searchCustomerPlaceholder: "Type to search customer...",
      enterCustomerManually: "Or enter manually (will create new customer if not exists)",
      typeCustomerName: "Type customer name...",
      createCustomerWithDetails: "Create New Customer with Details",
      selectCustomer: "Select Customer",
      saleItems: "Sale Items",
      addItem: "Add Item",
      item: "Item",
      selectItem: "Select Item",
      quantity: "Quantity",
      pricePerUnit: "Price per Unit",
      stockType: "Stock Type",
      pressStock: "Press Stock",
      homeStock: "Home Stock",
      flagSize: "Flag Size",
      flagSizeExample: "e.g., 3x5 ft",
      qualityDesignType: "Quality/Design Type",
      qualityDesignExample: "e.g., Premium, Standard",
      total: "Total",
      subtotal: "Subtotal",
      financial: "Financial Details",
      discount: "Discount",
      tax: "Tax",
      netAmount: "Net Amount",
      status: "Status",
      paymentStatus: "Payment Status",
      draft: "Draft",
      confirmed: "Confirmed",
      cancelled: "Cancelled",
      paid: "Paid",
      unpaid: "Unpaid",
      partial: "Partial",
      payment: "Payment",
      addPayment: "Add Payment",
      paymentMethod: "Payment Method",
      amountPaid: "Amount Paid",
      notes: "Notes",
      additionalNotesPlaceholder: "Additional notes...",
      totalPaid: "Total Paid",
      balanceDue: "Balance Due",
      saleCreated: "Sale created successfully",
      saleConfirmed: "Sale confirmed and stock deducted",
      saleCancelled: "Sale cancelled successfully",
      paymentAdded: "Payment added successfully",
      failedToCreate: "Failed to create sale",
      failedToConfirm: "Failed to confirm sale",
      failedToCancel: "Failed to cancel sale",
      failedToLoad: "Failed to load sales",
      confirmSaleMessage: "This will deduct stock from inventory. Continue?",
      cancelSaleMessage: "This will return stock to inventory. Continue?",
      stockDeducted: "Stock will be deducted when sale is confirmed.",
      noStockDeduction: "No stock deduction on draft sales",

      detailsTitle: "Sale #{{id}}",
      saveSaleDate: "Save date",
      saleDateHelp:
        "Change the date on the bill, or clear the field and save to leave the bill date blank (shown as —).",
      saleDateUpdated: "Sale date updated",
      saleDateUpdateFailed: "Failed to update sale date",
      itemSizeColumn: "Size",
      customerColumn: "Customer",
      phoneColumn: "Phone",
      paymentColumn: "Payment",
      noPaymentsYet: "No payments",
      paymentHistory: "Payment History",
      amount: "Amount",
      paymentLine: "#{{id}}: {{amount}} ({{method}})",
      editBill: "Edit bill",
      editBillTooltipDraft: "Edit line items and totals",
      editBillTooltipConfirmed: "Edit items (will revert sale to draft)",
      totalsSubtotal: "Subtotal",
      totalsDiscount: "Discount",
      totalsTax: "Tax",
      totalsNet: "Net",
      totalsPaid: "Paid",
      totalsDue: "Due",
      modalConfirmSaleTitle: "Confirm sale",
      modalCancelSaleTitle: "Cancel sale",
      saleCancelConfirmBody:
        "Are you sure you want to cancel this sale? This will return stock to inventory when applicable.",
      cancelSaleAction: "Cancel sale",
      editPaymentTitle: "Edit payment",
      editPaymentAmountLabel: "Amount *",
      editPaymentUpdateButton: "Update payment",
      paymentUpdatedSuccess: "Payment updated successfully",
      paymentUpdateFailed: "Failed to update payment. Please try again.",
      paymentCancelledSuccess: "Payment removed successfully",
      paymentCancelFailed: "Failed to remove payment. Please try again.",
      cancelPaymentTitle: "Remove payment",
      invalidPaymentAmount: "Please enter a valid payment amount",
      paymentModalAmountToPay: "Amount to pay: {{amount}}",
      paymentModalBreakdown: "Net: {{net}} − Paid: {{paid}} = Due: {{due}}",
      paymentModalMaxPlaceholder: "Max: {{amount}}",
      paymentModalBalanceDue: "Balance due: {{amount}}",
      failedToAddPayment: "Failed to add payment",

      editSaleTitle: "Edit sale #{{id}}",
      editSaleCustomerLabel: "Customer *",
      editSaleSelectCustomerPlaceholder: "Select customer",
      editSaleItemsLabel: "Items *",
      editSaleQuantityPlaceholder: "Quantity",
      editSalePricePlaceholder: "Price",
      editSaleCancelledCannotEdit: "Cancelled sales cannot be edited",
      editSaleLoadFailed: "Failed to load sale",
      editSaleUpdatedSuccess: "Sale saved. Status set to Draft.",
      editSaleUpdateFailed: "Failed to update sale",
      editSaleSubmit: "Update sale",

      // Direct sales (no stock) feature
      totalAmount: "Total Amount",
      profit: "Profit",
      directSalesTitle: "Direct Sales",
      directSalesSubtitle: "Items bought externally & sold directly",
      newDirectSale: "New Direct Sale",
      noDirectSalesFound: "No direct sales found",
      createFirstDirectSale: "Create First Direct Sale",

      createDirectSaleTitle: "Create Direct Sale",
      createDirectSaleSubtitle: "Buy from external source & sell directly (No stock)",
      customerInformation: "Customer Information",
      customerNameRequiredLabel: "Customer Name *",
      selectCustomerOptional: "Select from list (optional)",
      selectCustomerPlaceholder: "Select customer...",
      itemsToSell: "Items to Sell",
      sellingPricePerUnit: "Selling price / unit *",
      costPricePerUnit: "Cost Price *",
      purchaseCostPerUnit: "Purchase cost / unit",
      costNotSetShort: "Not set",
      costNotSetInInventory:
        "Purchase cost is not set on this inventory item. Add Cost price on the item in Inventory to see line profit.",
      lineProfitAtYourPrice: "Line profit at your selling price:",
      lineProfitHint: "quantity × (selling price − purchase cost)",
      profitIfSoldAtCost: "If you sell at purchase cost (AFN {{cost}} / unit), profit on this line:",
      forThisLineQty: "(quantity {{qty}})",
      totalPurchaseCost: "Total purchase cost",
      profitSummaryFootnote:
        "Profit = net amount − total purchase cost. Uses each item’s Cost price in Inventory when set; otherwise the latest unit cost from a press supplier purchase for that item. Lines with neither are excluded from the purchase total.",
      listPriceReference: "List price",
      purchaseCostSourceInventory: "Source: cost price saved on the inventory item.",
      purchaseCostSourceSupplier: "Source: latest unit cost from a press supplier purchase for this item.",
      purchaseCostNoCostListPriceHint: "No purchase cost on file — list/selling price from inventory is shown for reference only.",
      lineProfitNeedsCostOrUseList:
        "Add a cost price on the item or record a press purchase with this item to compute profit. List price on file: AFN {{list}} / unit (not used as cost).",

      createSaleDraft: "Create Sale (Draft)",
      selectOrEnterCustomer: "Select a customer from the list or type a name",
      failedToLoadOrderData: "Failed to load order data for this sale",
      quantityMustBeGreaterThanZero: "Quantity must be greater than zero",
      priceMustBeGreaterThanZero: "Selling price must be greater than zero",
      itemNumber: "Item {{n}}",
      insufficientStockForItem:
        "Insufficient {{stockType}} on line {{itemNumber}}. Available: {{available}}.",
      apiErrorInsufficientStockForItem:
        "{{stockType}} is not enough for «{{itemName}}». Available: {{available}}, requested: {{required}}.",
      apiErrorInsufficientStockSimple: "Not enough {{stockType}}. Available: {{available}}.",
      apiErrorInsufficientStockAdjustment: "Not enough stock for this adjustment.",
      apiErrorFailedToRevertStock:
        "Stock could not be restored while saving this sale. Please try again or contact support.",
      apiErrorSaleAlreadyConfirmed: "This sale is already confirmed.",
      apiErrorCannotConfirmCancelledSale: "A cancelled sale cannot be confirmed.",
      apiErrorSaleAlreadyCancelled: "This sale is already cancelled.",
      apiErrorDirectSaleAlreadyConfirmed: "This direct sale is already confirmed.",
      apiErrorPaymentMustBePositive: "Payment amount must be greater than zero.",
      costPriceNonNegative: "Cost price cannot be negative.",
      totalStockLabel: "Total stock",
      stockLabelGeneric: "Stock",
      quantityRequired: "Quantity must be greater than 0",
      priceRequired: "Selling price must be greater than 0",
      directSaleUpdatedAndConfirmed: "Direct sale updated and confirmed.",
      availableStock: "Available stock",
      customerNameRequired: "Customer name is required",
      customerCreatedSuccessfully: "Customer created successfully",
      failedToCreateCustomer: "Failed to create customer",
      createNewCustomer: "Create new customer",
      enterCustomerName: "Enter customer name",
      enterPhoneNumber: "Enter phone number",
      enterAddress: "Enter address",
      createCustomer: "Create customer",

      supplierNameOptionalLabel: "Supplier/Source (optional)",
      financialSummary: "Financial Summary",
      subtotalSelling: "Subtotal (Selling):",
      totalCostBuying: "Total Cost:",
      createDirectSaleDraft: "Create Direct Sale (Draft)",
      confirmDirectSale: "Confirm & Generate Bill",
      failedToCreateDirectSale: "Failed to create direct sale",
      failedToLoadDirectSales: "Failed to load direct sales",
      failedToFetchCustomers: "Failed to fetch customers",
      directSaleCreated: "Direct sale created successfully",
      directSaleUpdated: "Direct sale updated successfully",
      directSaleConfirmed: "Direct sale confirmed successfully",
      editDirectSaleTitle: "Edit Direct Sale",
      editDirectSaleSubtitle: "Update direct sale details",
      updateDirectSale: "Update Direct Sale",
      updateAndConfirm: "Update & Confirm"
    },
    customers: {
      title: "Customers",
      subtitle: "Manage customer information",
      addCustomer: "Add Customer",
      searchPlaceholder: "Search customers by name or phone...",
      searchPlaceholderSerial: "Search by name, phone, or serial no....",
      manualSerialNo: "Serial No.",
      manualSerialNoPlaceholder: "Ledger / reference number (optional)",
      show: "Show",
      perPage: "per page",
      noCustomersFound: "No customers found",
      name: "Name",
      phone: "Phone",
      address: "Address",
      email: "Email",
      date: "Date",
      actions: "Actions",
      view: "View",
      ledgerLabel: "Ledger",
      edit: "Edit",
      delete: "Delete",
      noEmail: "No email",
      deletedSuccess: "deleted successfully",
      failedToDelete: "Failed to delete customer",
      notFound: "Customer not found",
      editCustomer: "Edit Customer",
      addCustomerForm: "Add Customer",
      updateInfo: "Update customer information",
      createNew: "Create a new customer",
      nameRequired: "Name is required",
      phoneRequired: "Phone is required",
      addressRequired: "Address is required",
      invalidEmail: "Invalid email format",
      enterName: "Enter customer name",
      enterPhone: "Enter phone number",
      enterEmailOptional: "Enter email address (optional)",
      enterAddress: "Enter customer address",
      previousBalance: "Previous balance",
      previousBalanceRemaining: "Remaining previous balance",
      noPreviousBalance: "No previous balance remaining for this customer.",
      enterValidAmount: "Please enter a valid amount.",
      previousBalancePaid: "Previous balance payment recorded successfully.",
      amountPlaceholder: "Amount",
      payingPreviousBalance: "Paying...",
      payPreviousBalance: "Pay Previous Balance",
      editRemainingPrevBalanceTitle: "Edit remaining previous balance",
      editRemainingPrevBalanceLabel: "Remaining amount owed (AFN) *",
      editRemainingPrevBalanceHelp:
        "You are editing only the amount still owed (the large figure). The opening-balance total is recalculated as: paid to date + this remaining amount — the “Original” line will update to match.",
      prevBalanceRemainingUpdated: "Previous balance updated from the remaining amount you entered.",
      prevBalancePaymentDeletedRestored:
        "Previous balance payment removed. That amount is added back to what the customer owes on previous balance.",
      hasDue: "Has outstanding due",
      notes: "Notes",
      notesOptional: "Additional notes (optional)",
      saving: "Saving...",
      updateCustomer: "Update Customer",
      createCustomer: "Create Customer",
      ledger: {
        failedToFetch: "Failed to fetch customer data",
        selectOrder: "Please select at least one order",
        validAmount: "Please enter a valid payment amount",
        amountExceeds: "Payment amount (AFN {{amount}}) exceeds total due (AFN {{total}})",
        paymentSuccess: "Payment recorded successfully for {{count}} order(s)",
        paymentFailed: "Failed to record payment",
        notFound: "Customer not found",
        exportPdf: "Export PDF",
        exportPdfTitle: "Export to PDF",
        recordPayment: "Record Payment",
        totalOrders: "Total Orders",
        totalPaid: "Total Paid",
        totalDue: "Total Due",
        totalBilled: "Total billed (orders & sales)",
        activityHistory: "Orders & sales",
        ref: "Ref",
        entryType: "Type",
        typeOrder: "Order",
        typeSale: "Sale",
        typeDirectSale: "Direct sale",
        itemsShort: "items",
        viewSale: "View sale",
        viewDirectSale: "View direct sale",
        paymentLine: "Payment #{{n}}",
        paymentKind: "Payment",
        noActivity: "No orders or sales recorded for this customer yet.",
        orderHistory: "Order History",
        orderId: "Order ID",
        items: "Items",
        paid: "Paid",
        lastPayment: "Last Payment",
        viewOrder: "View Order",
        printBill: "Print Bill",
        cancelOrder: "Cancel Order",
        showing: "Showing",
        to: "to",
        of: "of",
        paymentMode: "Payment Mode",
        fullPayment: "Full Payment",
        partialPayment: "Partial Payment",
        selectOrders: "Select Orders",
        deselectAll: "Deselect All",
        selectAll: "Select All",
        noOrdersAvailable: "No orders available",
        ordersSelected: "{{count}} order(s) selected",
        selectInvoiceEntry: "Please select at least one order or sale with a balance.",
        paymentSuccessInvoices: "Payment recorded for {{count}} invoice(s).",
        paymentReceiptMultiple: "Payment for {{count}} invoices",
        selectInvoices: "Select invoices (orders & sales)",
        noOutstandingInvoices: "No orders or sales with an outstanding balance.",
        invoicesSelected: "{{count}} invoice(s) selected",
        total: "Total",
        totalDueLabel: "Total Due",
        amountLabel: "Amount",
        autoCalculated: "(Auto-calculated)",
        max: "Max",
        paymentMethod: "Payment Method",
        reference: "Reference",
        optional: "Optional",
        processing: "Processing...",
        printBillTitle: "Print Bill",
        cancelOrderConfirm: "Are you sure you want to cancel Order #{{id}}? This action cannot be undone and will return the items to inventory.",
        keepOrder: "Keep Order",
        cancelling: "Cancelling...",
        pdfPluginError: "PDF plugin not loaded. Please refresh the page.",
        loadOrderError: "Failed to load order details",
        editPrevBalancePaymentHint:
          "Enter the amount for this payment line only. Reducing it increases the customer's remaining previous balance by the difference (the unpaid portion goes back to balance owed).",
        editPrevBalancePaymentAmountLabel: "This payment amount (AFN)",
        afterEditRemainingPrevBalance: "Remaining previous balance after save (estimate)",
        perPage: "per page",
        prevBalancePaymentsSection: "Previous balance payments",
        prevBalancePaymentsSectionHint: "Recorded payments toward the opening balance (5 per page).",
        noPrevBalancePayments: "No previous balance payments yet.",
        paidTowardPrevBalance: "Paid toward previous balance",
        openingTotalOnFile: "Opening total on file",
        addPreviousBalanceShort: "Add previous balance",
        editOrderPaymentTitle: "Edit order payment",
        editSalePaymentTitle: "Edit sale payment",
        editDirectSalePaymentTitle: "Edit direct sale payment",
        balancePaymentUpdated: "Previous balance payment updated.",
        balancePaymentUpdateFailed: "Failed to update previous balance payment.",
        balancePaymentDeleteFailed: "Failed to delete previous balance payment.",
        paymentUpdatedSuccess: "Payment updated successfully.",
        paymentUpdateFailed: "Failed to update payment.",
        paymentDeletedSuccess: "Payment deleted successfully.",
        paymentDeleteFailed: "Failed to delete payment.",
        deletePaymentTitle: "Delete payment",
        deleteOrderPaymentConfirm:
          "Remove this payment from the order? The order balance will be updated.",
        deleteSalePaymentConfirm:
          "Remove this payment from the sale? The sale balance will be updated.",
        deleteDirectSalePaymentConfirm:
          "Remove this payment from the direct sale? The balance will be updated.",
        deletePrevBalancePaymentConfirm:
          "Remove this previous balance payment? The customer's remaining opening balance will be adjusted."
      }
    },
    inventory: {
      title: "Inventory",
      addItem: "Add Item",
      searchPlaceholder: "Search items by name or SKU...",
      allTypes: "All Types",
      rawMaterial: "Raw Material",
      finishedProduct: "Finished Product",
      noItemsFound: "No items found",
      name: "Name",
      sku: "SKU",
      type: "Type",
      stock: "Stock",
      minStock: "Min Stock",
      price: "Price",
      status: "Status",
      actions: "Actions",
      lowStock: "Low Stock",
      inStock: "In Stock",
      addItemPageTitle: "Add Item",
      editItemPageTitle: "Edit Item",
      addItemPageSubtitle: "Create a new inventory item",
      editItemPageSubtitle: "Update inventory item",
      failedToFetch: "Failed to fetch inventory items",
      failedToDelete: "Failed to delete item",
      formHeaderCreate: "Add item — new inventory record",
      formItemNameLabel: "Item name *",
      formItemNamePlaceholder: "Enter item name",
      formTypeLabel: "Type *",
      formCategoryLabel: "Category *",
      formSelectCategory: "Select category...",
      formAddCategoryTitle: "Add new category",
      formNewCategoryPlaceholder: "New category name",
      formUnitPriceLabel: "Selling price (per unit)",
      formUnitPricePlaceholder: "Enter selling price",
      purchasePriceLabel: "Purchase price (per unit)",
      profitPerUnitLabel: "Profit per unit",
      profitPerUnitHint: "Selling − purchase (same quantity)",
      formFlagDesignLabel: "Flag design type",
      formSelectDesignOptional: "Select design type (optional)",
      formAddDesignTitle: "Add new design type",
      formNewDesignPlaceholder: "New design type name",
      formPressStockLabel: "Press stock *",
      formPressStockPlaceholder: "Enter press stock",
      formHomeStockLabel: "Home stock *",
      formHomeStockPlaceholder: "Enter home stock",
      formMinStockLabel: "Minimum stock *",
      formMinStockPlaceholder: "Enter minimum stock level",
      formDescriptionLabel: "Description",
      formDescriptionPlaceholder: "Enter item description (optional)",
      formSkuNote: "SKU will be generated automatically when you save.",
      formCreateButton: "Create item",
      formUpdateButton: "Update item",
      errNameRequired: "Item name is required",
      errCategoryRequired: "Category is required",
      errPressStock: "Press stock must be 0 or greater",
      errHomeStock: "Home stock must be 0 or greater",
      errMinStock: "Minimum stock must be 0 or greater",
      toastCategoryAdded: "Category added successfully",
      toastCategoryAddFailed: "Failed to add category",
      toastDesignAdded: "Design type added successfully",
      toastDesignAddFailed: "Failed to add design type",
      toastFetchItemFailed: "Failed to fetch item details",
      toastItemUpdated: "Item updated successfully",
      toastItemCreated: "Item created successfully",
      toastSaveFailed: "Failed to save item",
      detailsPageSubtitle: "Item details",
      detailsNotFound: "Item not found",
      detailsBackToList: "Back to inventory",
      detailsEdit: "Edit",
      detailsDelete: "Delete",
      detailsDeleteConfirm: "Are you sure you want to delete this item?",
      toastItemDeleted: "Item deleted successfully",
      toastItemDeleteFailed: "Failed to delete item",
      detailsSectionInfo: "Item information",
      detailsSectionDescription: "Description",
      detailsTableId: "ID",
      detailsTableCategory: "Category",
      detailsTableUnitPrice: "Unit price",
      detailsTableCurrentStock: "Current stock",
      detailsTableMinStock: "Minimum stock",
      detailsTableCreated: "Created",
      detailsTableUpdated: "Updated"
    },
    purchases: {
      title: "Purchases",
      addPurchase: "Add Purchase",
      item: "Item",
      purchaseDate: "Purchase Date",
      searchPlaceholder: "Search purchases by item or supplier...",
      noPurchasesFound: "No purchases found",
      supplier: "Supplier",
      itemName: "Item Name",
      cost: "Cost",
      date: "Date",
      paymentStatus: "Payment Status",
      allStatus: "All Status",
      paid: "Paid",
      partial: "Partial",
      due: "Due",
      addPurchasePageTitle: "Add Purchase",
      editPurchasePageTitle: "Edit Purchase",
      addPurchasePageSubtitle: "Create a new purchase",
      editPurchasePageSubtitle: "Update purchase details",
      failedToFetch: "Failed to fetch purchases",
      failedToDelete: "Failed to delete purchase",
      quantity: "Quantity",
      paidAmount: "Paid",
      remaining: "Remaining",

      // Create Purchase form – labels/placeholders
      formSupplierLabel: "Supplier *",
      formSelectExistingSupplier: "Select existing supplier...",
      formOrLabel: "OR",
      formTypeNewSupplier: "Type new supplier name...",

      formItemLabel: "Item",
      formSelectExistingItem: "Select existing item...",
      formOrTypeManually: "OR type manually below",
      formItemNameLabel: "Item Name *",
      formItemNamePlaceholder: "Enter item name",

      formQuantityWithUnitLabel: "Quantity (with unit) *",
      formQuantityExamples: "e.g., 10 meter, 5.5 kg, 100 pieces",

      formCostPerItemLabel: "Cost Per Item *",
      formCostPerItemPlaceholder: "Enter cost per item",

      formForPressLabel: "This is for press (production)",
      splitStockLabel: "Split stock (must equal total quantity: {{total}}) *",
      split5050: "Split 50/50",
      splitHelpText: "Enter how many units go to press stock and how many to home stock (e.g. 25 + 25 = 50).",
      formForPressWarning: "To update inventory stock, please also select an item from the dropdown above.",

      formDescriptionLabel: "Description",
      formDescriptionPlaceholder: "Enter description (optional)",
      formDescriptionPlaceholderPersonal: "Enter description (required for personal purchases)",

      formPaymentMethodLabel: "Payment Method",
      formCashLabel: "Cash",

      formReferenceNumberLabel: "Reference Number",
      formReferenceNumberPlaceholder: "Enter reference number (optional)",

      formCreateButton: "Create",
      formCancelButton: "Cancel",
      formBillNumberLabel: "Bill number",
      formBillNumberPlaceholder: "Enter supplier bill number",
      formSelectItemOptional: "Select item (optional)...",
      formCalculation: "Calculation:",
      formTotalCost: "Total cost: {{amount}}",
      formQtyTimesCost: "{{qty}} × {{cost}} = {{total}}",
      formSplitBetweenStocks: "Split quantity between home stock and press stock",
      formHomeQtyLabel: "Home stock quantity *",
      formPressQtyLabel: "Press stock quantity *",
      formHomeQtyPlaceholder: "Enter home quantity",
      formPressQtyPlaceholder: "Enter press quantity",
      formDistribution: "Distribution:",
      formStockSelectHint: "Choose which stock this purchase adds to",
      formStockTypeLabel: "Stock type *",
      formPressCheckboxPersonal: "Check \"For press (production)\" to update inventory.",
      formDescriptionPersonalRequired: "Description is required for personal purchases",
      formPaymentCard: "Card",
      formPaymentBank: "Bank transfer",
      formPaymentCheck: "Check",
      formUpdatePurchase: "Update purchase",
      formCreatePurchase: "Create purchase",
      errSupplierRequired: "Supplier is required",
      errItemNameRequired: "Item name is required",
      errQuantityRequired: "Quantity is required",
      errHomeQtyNegative: "Home quantity cannot be negative",
      errPressQtyNegative: "Press quantity cannot be negative",
      errSplitMismatch: "Home ({{home}}) + press ({{press}}) must equal total ({{total}})",
      errCostPositive: "Cost must be greater than 0",
      toastFetchFailed: "Failed to fetch purchase details",
      toastUpdated: "Purchase updated successfully",
      toastCreated: "Purchase created successfully",
      toastSaveFailed: "Failed to save purchase",
      toastDeleted: "Purchase deleted successfully",
      formPurchaseItemsTitle: "Purchase Items",
      formAddItem: "Add Item",
      formSelectItem: "Select Item",
      formUnitCost: "Unit Cost",
      formLineTotal: "Line Total",
      formTotalCostSimple: "Total Cost",
      formPartialPayment: "Partial Payment",
      formFullCredit: "Full Credit (No Payment)",
      formPaymentHintCash: "Pay full amount now",
      formPaymentHintPartial: "Pay partial amount now, rest later",
      formPaymentHintCredit: "No payment now, pay later",
      formPaymentAmount: "Payment Amount",
      formPaymentAmountPlaceholder: "Enter amount to pay now",
      errAtLeastOneItem: "At least one item is required",
      errEachItemValid: "Each item must have name, quantity > 0, and valid unit cost",
      errPartialPaymentRequired: "Payment amount is required for partial payment",
      errPartialPaymentLessThanTotal: "Partial payment must be less than total cost",
      printBill: "Print Bill",
      purchaseBill: "Purchase Bill",
      purchaseInformation: "Purchase Information",
      billNumber: "Bill Number",
      forPress: "For Press",
      paymentHistory: "Payment History",
      purchaseNotFound: "Purchase not found",
      backToPurchases: "Back to Purchases",
      amount: "Amount",
      method: "Method",
      reference: "Reference"
    },
    suppliers: {
      title: "Suppliers",
      addSupplier: "Add Supplier",
      contact: "Contact",
      searchPlaceholder: "Search suppliers by name, phone, or email...",
      noSuppliersFound: "No suppliers found",
      name: "Name",
      phone: "Phone",
      address: "Address",
      email: "Email",
      failedToFetch: "Failed to fetch suppliers",
      failedToDelete: "Failed to delete supplier",
      editSupplier: "Edit Supplier",
      addSupplierForm: "Add Supplier",
      updateInfo: "Update supplier information",
      createNew: "Create a new supplier.",
      supplierNameRequired: "Supplier name is required",
      enterSupplierName: "Enter supplier name",
      contactPerson: "Contact Person",
      enterContactPerson: "Enter contact person name",
      enterPhone: "Enter phone number",
      enterEmail: "Enter email address",
      enterAddress: "Enter supplier address",
      saving: "Saving...",
      updateSupplier: "Update Supplier",
      createSupplier: "Create Supplier",
      ledger: "Supplier ledger",
      failedToFetchDetails: "Failed to fetch supplier details",
      supplierUpdated: "Supplier updated successfully",
      supplierCreated: "Supplier created successfully",
      failedToSave: "Failed to save supplier",
      billHistory: "Bill History",
      openingBalanceBill: "Opening balance",
      openingBalanceBillDesc: "Legacy balance before system (see payments below)",
      prevBalancePayment: "Previous Balance Payment",
      selectBillRequired: "Please select at least one bill",
      paySpecificBills: "Pay Specific Bills",
      payAllDue: "Pay All Due",
      fixedAmount: "Pay Fixed Amount",
      card: "Card",
      bankTransfer: "Bank Transfer",
      check: "Check",
      selectBills: "Select Bills",
      addPreviousBalance: "Add Previous Balance",
      editPreviousBalance: "Edit Previous Balance",
      deletePrevBalancePayment: "Delete Previous Balance Payment",
      confirmDeletePrevBalancePayment: "Are you sure you want to delete this previous balance payment?",
      deletePayment: "Delete Payment",
      confirmDeletePayment: "Are you sure you want to delete this payment?",
      updatePrevBalancePayment: "Enter updated previous balance payment amount",
      updatePaymentAmount: "Enter updated payment amount",
      prevBalancePaymentUpdated: "Previous balance payment updated successfully",
      prevBalancePaymentUpdateFailed: "Failed to update previous balance payment",
      prevBalancePaymentDeleted: "Previous balance payment deleted successfully",
      prevBalancePaymentDeleteFailed: "Failed to delete previous balance payment"
    },
    items: {
      name: "Item Name",
      enterName: "Enter item name",
      nameRequired: "Item name is required"
    },
    employees: {
      title: "Employees",
      subtitle: "Manage employee information",
      addEmployee: "Add Employee",
      editEmployee: "Edit Employee",
      employeeDetails: "Employee Details",
      searchPlaceholder: "Search employees by name, NID, or phone...",
      noEmployeesFound: "No employees found",
      notFound: "Employee not found",
      backToEmployees: "Back to Employees",
      name: "Name",
      nid: "NID",
      phone: "Phone",
      position: "Position",
      salary: "Salary",
      active: "Active",
      inactive: "Inactive",
      exportPdf: "Export PDF",
      monthlySalary: "Monthly Salary",
      salaryHistory: "Salary history",
      salaryEffectiveFrom: "Current rate from {{date}}",
      balanceOwed: "Balance Owed",
      pendingAdvances: "Pending Advances",
      netSalary: "Net Salary",
      totalLoans: "Total Loans",
      totalTips: "Total Tips",
      details: "Details",
      advances: "Advances",
      salaryPayments: "Salary Payments",
      loans: "Loans",
      tabMobileDetails: "Details",
      tabMobileAdvances: "Advances",
      tabMobileSalary: "Salary",
      tabMobileLoans: "Loans",
      deposit: "Salary deposit",
      tabMobileDeposit: "Deposit",
      depositBalance: "Amount you are holding for this employee",
      depositHelp: "When they return part of their salary for you to keep, record a deposit. When they need the money, pay it out from the same tab (cannot exceed the balance).",
      depositHoldTitle: "Employee deposited (hold with company)",
      depositPayoutTitle: "Pay back to employee",
      recordHold: "Record deposit",
      recordPayout: "Pay from deposit",
      entryTypeHold: "Deposit",
      entryTypePayout: "Paid out",
      noDepositEntries: "No deposit activity yet",
      depositAmountRequired: "Enter a valid amount greater than zero.",
      depositHoldSaved: "Deposit recorded.",
      depositPayoutSaved: "Payout recorded.",
      depositSaveFailed: "Could not save deposit entry.",
      depositEntryUpdated: "Deposit entry updated.",
      depositEditTitle: "Edit deposit entry",
      printDepositSlip: "Print slip",
      personalInformation: "Personal Information",
      fatherName: "Father Name",
      employmentInformation: "Employment Information",
      joinDate: "Join Date",
      fromToToday: "From {{date}} to today",
      totalTipsReceived: "Total Tips Received",
      addTip: "Add Tip",
      advancesAutoDeducted: "Advances are automatically deducted from salary when salary is paid",
      addAdvance: "Add Advance",
      amount: "Amount",
      amountAfnRequired: "Amount (AFN) *",
      deductionDate: "Deduction Date",
      returnPlan: "Return Plan",
      deducted: "Deducted",
      pending: "Pending",
      noAdvancesRecorded: "No advances recorded",
      unpaidMonths: "{{count}} unpaid month(s) - Balance: {{balance}}",
      paySalary: "Pay Salary",
      month: "Month",
      baseSalary: "Base Salary",
      advanceDeducted: "Advance Deducted",
      netPaid: "Net Paid",
      paymentDate: "Payment Date",
      notes: "Notes",
      noSalaryPaymentsRecorded: "No salary payments recorded",
      loansNoteTitle: "Note:",
      loansNote: "Loans are separate from advances and do not decrease from salary. They should be tracked and repaid separately.",
      addLoan: "Add Loan",
      paid: "Paid",
      remaining: "Remaining",
      interestRate: "Interest Rate",
      repaymentPlan: "Repayment Plan",
      noLoansRecorded: "No loans recorded",
      loanStatusPaid: "Paid",
      loanStatusPartial: "Partial",
      loanStatusActive: "Active",
      editAdvance: "Edit Advance",
      dateGiven: "Date Given",
      dateRequired: "Date *",
      returnPlanNote: "(Note: Advances are automatically deducted from salary when salary is paid)",
      returnPlanPlaceholder: "e.g., This will be deducted from the salary",
      additionalNotes: "Additional notes",
      notesOptionalPlaceholder: "Additional notes (optional)",
      createAdvance: "Create Advance",
      updateAdvance: "Update Advance",
      recordSalaryPayment: "Record Salary Payment",
      salaryType: "Salary Type",
      monthly: "Monthly",
      weekly: "Weekly",
      weeklySalaryApprox: "Weekly Salary (approx)",
      weekStartDate: "Week Start Date",
      weeklyPaidDateLabel: "Payment date *",
      weeklyPaidDateHelp:
        "Set which calendar date this weekly payment is for. With Dari or Pashto, you pick in Solar Hijri (Afghan months); it is saved as the matching Gregorian date.",
      paymentDateGregorianHint: "Gregorian",
      payAllDueBalance: "Pay All Due Balance ({{count}} months)",
      totalGross: "Total Gross: {{amount}}",
      pendingAdvancesWillDeduct: "- Pending Advances: {{amount}}",
      netAmount: "= Net Amount: {{amount}}",
      selectMonthsToPay: "Select Months to Pay",
      noUnpaidMonthsFound: "No unpaid months found",
      selectedMonths: "Selected: {{count}} month(s)",
      grossAmount: "Gross Amount: {{amount}}",
      pendingAdvancesInfo: "Pending Advances: {{amount}} will be automatically deducted from the salary payment(s).",
      monthlySalaryLabel: "Monthly Salary:",
      weeklyApproxLabel: "Weekly (approx):",
      payWeeklySalary: "Pay Weekly Salary",
      payAllMonths: "Pay All ({{count}} months)",
      paySelectedMonths: "Pay Selected ({{count}} months)",
      selectMonthsToPayButton: "Select Months to Pay",
      editLoan: "Edit Loan",
      loanAmount: "Loan Amount (AFN) *",
      loanPrincipalLabel: "Loan amount",
      loanDate: "Loan Date *",
      interestRatePercent: "Interest Rate (%)",
      howWillLoanBeRepaid: "How will this loan be repaid?",
      createLoan: "Create Loan",
      updateLoan: "Update Loan",
      recordLoanPayment: "Record Loan Payment",
      alreadyPaid: "Already Paid",
      remainingAmount: "Remaining Amount",
      paymentAmount: "Payment Amount (AFN) *",
      recordPayment: "Record Payment",
      addTipTitle: "Add Tip",
      tipAmount: "Amount (AFN) *",
      reason: "Reason",
      reasonPlaceholder: "e.g., Good work, Extra effort",
      deleteAdvance: "Delete Advance",
      deleteLoan: "Delete Loan",
      deleteConfirmMessage: "Are you sure you want to delete this {{type}}? This action cannot be undone.",
      advanceNoun: "advance",
      loanNoun: "loan",
      editSalaryPayment: "Edit Salary Payment",
      updatePayment: "Update Payment",
      employeeIdLabel: "ID",
      editPaymentTitle: "Edit Payment",
      deletePaymentTitle: "Delete Payment",
      recordPaymentActionTitle: "Record Payment",
      printReportTitle: "Employee Details Report",
      printDocumentTitle: "Employee Details - {{name}}",
      financialSummary: "Financial Summary",
      employeeInformation: "Employee Information",
      metricCol: "Metric",
      amountAfnCol: "Amount (AFN)",
      totalAdvancesLabel: "Total Advances",
      totalSalaryPaidLabel: "Total Salary Paid",
      totalAdvancesDeductedLabel: "Total Advances Deducted",
      reportDate: "Report Date",
      weeklySalaryNotesDefault: "Weekly salary payment",
      bulkPaymentMonthsNote: "Bulk payment for {{count}} months",
      addEmployeePageTitle: "Add Employee",
      editEmployeePageTitle: "Edit Employee",
      addEmployeePageSubtitle: "Create a new employee",
      editEmployeePageSubtitle: "Update employee information",
      enterName: "Enter employee name",
      enterFatherName: "Enter father name",
      enterNid: "Enter NID number",
      enterPhone: "Enter phone number",
      enterSalary: "Enter salary amount",
      enterAddress: "Enter employee address",
      fatherName: "Father Name",
      nid: "NID",
      salary: "Salary",
      joinDate: "Join Date",
      activeEmployee: "Active Employee",
      updateEmployee: "Update Employee",
      createEmployee: "Create Employee",
      nameRequired: "Name is required",
      fatherNameRequired: "Father name is required",
      nidRequired: "NID is required",
      phoneRequired: "Phone is required",
      addressRequired: "Address is required",
      salaryRequired: "Valid salary is required",
      joinDateRequired: "Join date is required",
      failedToSave: "Failed to save employee. Please try again.",
      failedToFetch: "Failed to fetch employees",
      failedToDelete: "Failed to delete employee",
      status: "Status",
      toast: {
        fetchFailed: "Failed to fetch employee data",
        advanceCreated: "Advance recorded successfully",
        advanceCreateFailed: "Failed to create advance",
        advanceUpdated: "Advance updated successfully",
        advanceUpdateFailed: "Failed to update advance",
        advanceDeleted: "Advance deleted successfully",
        loanDeleted: "Loan deleted successfully",
        deleteFailed: "Failed to delete",
        loanCreated: "Loan recorded successfully",
        loanCreateFailed: "Failed to create loan",
        loanUpdated: "Loan updated successfully",
        loanUpdateFailed: "Failed to update loan",
        loanDeletedSingle: "Loan deleted successfully",
        loanDeleteFailed: "Failed to delete loan",
        tipCreated: "Tip recorded successfully",
        tipCreateFailed: "Failed to record tip",
        weeklySalaryRecorded: "Weekly salary payment recorded successfully",
        selectMonthError: "Please select at least one month to pay",
        noMonthsSelected: "No months selected for payment",
        salaryRecordedMonths: "Salary payment recorded successfully for {{count}} month(s)",
        salaryCreateFailed: "Failed to create salary payment",
        invalidSalaryAmount: "Please enter a valid salary amount",
        salaryUpdated: "Salary payment updated successfully",
        salaryUpdateFailed: "Failed to update salary payment",
        salaryPaymentDateRequired: "Please choose a payment date",
        salaryDeleted: "Salary payment deleted successfully",
        salaryDeleteFailed: "Failed to delete salary payment",
        loanPaymentRecorded: "Loan payment recorded successfully",
        loanPaymentFailed: "Failed to record loan payment",
        pdfTableWarning: "PDF generated without tables. Please refresh page for full functionality.",
        pdfExportFailed: "Failed to export PDF: {{message}}"
      }
    },
    expenses: {
      title: "Expenses",
      addExpense: "Add Expense",
      addExpenseForm: "Add expense",
      searchPlaceholder: "Search description or notes...",
      allCategories: "All Categories",
      officeSupplies: "Office Supplies",
      utilities: "Utilities",
      transport: "Transportation",
      marketing: "Marketing",
      maintenance: "Maintenance",
      other: "Other",
      fromDate: "From Date",
      toDate: "To Date",
      total: "Total",
      dailyTotal: "Daily Total",
      description: "Description",
      category: "Category",
      amount: "Amount",
      date: "Date",
      actions: "Actions",
      deleteExpense: "Delete Expense",
      deleteConfirm: "Are you sure you want to delete this expense? This action cannot be undone.",
      failedToFetch: "Failed to fetch expenses",
      failedToDelete: "Failed to delete expense",
      deletedSuccess: "Expense deleted successfully",
      homeExpensesCard: "Home expenses",
      pressExpensesCard: "Press expenses",
      periodFilterHint: "Filter both cards and the list by period",
      tableFilterLabel: "Table:",
      showAllLocations: "All",
      filterHomeOnly: "Home only",
      filterPressOnly: "Press only",
      entriesInPeriod: "{{count}} entries",
      printHomeReport: "Print home expenses",
      printPressReport: "Print press expenses",
      printWindowTitle: "Expenses",
      minAmount: "Min amount",
      maxAmount: "Max amount",
      location: "Location",
      locationHome: "Home",
      locationPress: "Press",
      enterCategoryName: "Enter category name",
      addNewCategory: "Add New Category",
      amountPlaceholder: "0.00",
      categories: {
        office: "Office Supplies",
        utilities: "Utilities",
        transport: "Transportation",
        marketing: "Marketing",
        maintenance: "Maintenance",
        other: "Other"
      }
    },
    reportsPage: {
      title: "Reports & Analytics",
      subtitle: "Comprehensive business insights and analytics",
      refresh: "Refresh",
      detailed: "Detailed Report",
      ordersWithDues: "Orders with Dues",
      suppliersBalance: "Suppliers Balance",
      lowStock: "Low Stock Items",
      pendingAdvances: "Pending Advances",
      monthlyExpenses: "Monthly Expenses",
      employeeSalary: "Employee Salary",
      purchasePayments: "Purchase Payments",
      roznamcha: "Roz Namcha",
      failedToFetch: "Failed to fetch",
      noData: "No data",
      filters: "Filters",
      period: "Period",
      month: "Month",
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
      yearly: "Yearly",
      customRange: "Custom Range",
      startDate: "Start Date",
      endDate: "End Date",
      applyFilters: "Apply Filters",
      exportPdf: "PDF",
      exportExcel: "Excel",
      exportCsv: "CSV",
      exportToPdf: "Export to PDF",
      exportToExcel: "Export to Excel",
      exportToCsv: "Export to CSV",
      pdfExported: "PDF exported successfully",
      excelExported: "Excel file exported successfully",
      csvExported: "CSV exported successfully",
      exportFailed: "Failed to export",
      noDataForExport: "No data available for export",
      generated: "Generated",
      revenue: "Revenue",
      costs: "Costs",
      expenses: "Expenses",
      profit: "Profit",
      receivables: "Receivables",
      payables: "Payables",
      financialSummary: "Financial Summary",
      ordersSummary: "Orders Summary",
      purchasesSummary: "Purchases Summary",
      expensesSummary: "Expenses Summary",
      inventorySummary: "Inventory Summary",
      totalOrders: "Total Orders",
      totalRevenue: "Total Revenue",
      totalDue: "Total Due",
      totalPurchases: "Total Purchases",
      totalCost: "Total Cost",
      remaining: "Remaining",
      totalExpenses: "Total Expenses",
      count: "Count",
      totalItems: "Total Items",
      stockValue: "Stock Value",
      lowStockItems: "Low Stock Items",
      financialOverview: "Financial Overview",
      ordersByStatus: "Orders by Status",
      expensesByCategory: "Expenses by Category",
      directSalesSection: "Direct Sales (No Stock)",
      directSalesCount: "Direct Sales Count",
      directSalesRevenue: "Direct Sales Revenue",
      directSalesCost: "Direct Sales Cost",
      directSalesProfit: "Direct Sales Profit",
      directSalesDetails: "Direct Sales Details",
      saleId: "Sale ID",
      totalOutstanding: "Total Outstanding",
      ordersWithOutstandingDues: "order(s) with outstanding dues",
      orderId: "Order ID",
      customer: "Customer",
      phone: "Phone",
      total: "Total",
      paid: "Paid",
      due: "Due",
      date: "Date",
      status: "Status",
      totalOutstandingBalance: "Total Outstanding Balance",
      suppliersWithOutstandingBalance: "supplier(s) with outstanding balance",
      supplierId: "Supplier ID",
      name: "Name",
      contact: "Contact",
      balance: "Balance",
      created: "Created",
      itemsBelowMinimum: "Items below minimum stock level",
      itemId: "Item ID",
      sku: "SKU",
      type: "Type",
      currentStock: "Current Stock",
      minimum: "Minimum",
      unitPrice: "Unit Price",
      stockValueItem: "Stock Value",
      totalPendingAdvances: "Total Pending Advances",
      pendingAdvance: "pending advance(s)",
      advanceId: "Advance ID",
      employee: "Employee",
      amount: "Amount",
      dateGiven: "Date Given",
      returnPlan: "Return Plan",
      notes: "Notes",
      totalMonthlyExpenses: "Total Monthly Expenses",
      periodLabel: "Period",
      expense: "expense(s)",
      activeEmployees: "Active Employees",
      employeeId: "Employee ID",
      salary: "Salary",
      pendingAdvances: "Pending Advances",
      netSalary: "Net Salary",
      joinDate: "Join Date",
      purchasePaymentStatus: "Purchase Payment Status",
      totalPurchasesLabel: "Total purchases",
      paymentStatusSummary: "Payment Status Summary",
      purchaseId: "Purchase ID",
      supplier: "Supplier",
      item: "Item",
      quantity: "Quantity",
      cost: "Cost",
      purchaseDetails: "Purchase Details",
      roznamchaReport: "Roz Namcha Report",
      entries: "entries",
      itemName: "Item Name",
      description: "Description",
      costPrice: "Cost/Price",
      minDue: "Min Due",
      maxDue: "Max Due",
      minAmount: "Min amount",
      maxAmount: "Max amount",
      itemType: "Item Type",
      allTypes: "All Types",
      rawMaterial: "Raw Material",
      finishedProduct: "Finished Product",
      criticalOnly: "Critical Only",
      page: "Page",
      of: "of",
      category: "Category",
      totalAmount: "Total Amount",
      printingSummary: "Printing Press Summary",
      totalPrintingCost: "Total Printing Cost",
      printingDetails: "Printing Press Details",
      paymentStatus: "Payment Status",
      netAmount: "Net Amount"
    },
    recordLookup: {
      title: "Record Lookup",
      subtitle: "Search by customer/supplier name, ID, or phone",
      placeholder: "Enter name, ID, or phone number",
      enterQuery: "Please enter search text",
      searchFailed: "Failed to search records",
      matchedCustomers: "Matched Customers",
      matchedSuppliers: "Matched Suppliers",
      orders: "Orders",
      sales: "Sales",
      directSales: "Direct Sales",
      quotations: "Quotations",
      purchases: "Purchases"
    },
    sidebar: {
      switchToDari: "دری",
      switchToEnglish: "English"
    }
  },
  prs: {
    common: {
      add: "افزودن",
      edit: "ویرایش",
      delete: "حذف",
      save: "ذخیره",
      saving: "در حال ذخیره...",
      cancel: "لغو",
      clear: "پاک کردن",
      back: "بازگشت",
      search: "جستجو",
      actions: "اقدامات",
      view: "مشاهده",
      loading: "در حال بارگذاری...",
      noData: "داده‌ای یافت نشد",
      total: "مجموع",
      date: "تاریخ",
      type: "نوع",
      status: "وضعیت",
      name: "نام",
      phone: "شماره تماس",
      address: "آدرس",
      remove: "حذف",
      items: "اقلام",
      financial: "جزئیات مالی",
      noRecordsFound: "سابقه یافت نشد",
      all: "همه",
      prev: "قبلی",
      next: "بعدی",
      description: "توضیحات",
      amount: "مبلغ",
      price: "قیمت",
      show: "نمایش",
      cost: "هزینه",
      active: "فعال",
      inactive: "غیرفعال",
      lightMode: "حالت روشن",
      darkMode: "حالت تاریک",
      deletedSuccess: "با موفقیت حذف شد",
      notes: "یادداشت‌ها",
      notesPlaceholder: "یادداشت‌های اضافی...",
      print: "چاپ",
      printSize: "اندازه چاپ",
      retry: "تلاش مجدد",
      notAvailable: "ناموجود",
      unknownError: "خطای نامشخص",
      note: "توجه:",
      profit: "سود",
      calculation: "محاسبه:",
      payment: "پرداخت",
      remainingLabel: "باقیمانده",
      confirm: "تأیید",
      select: "انتخاب",
      close: "بستن",
      yes: "بله",
      no: "خیر",
      apply: "اعمال"
    },
    nav: {
      dashboard: "داشبورد",
      orders: "فرمایشات",
      customers: "مشریان",
      inventory: "ګدام",
      purchases: "خریداري",
      suppliers: "عرضه کننده ګان",
      employees: "کارمندان",
      expenses: "مصارفات",
      rozNamcha: "روز نامچه",
      rent: "کرایه",
      printingPress: "چاپخانه",
      reports: "راپور ها",
      bank: "بانک",
      recordLookup: "جستجوی سوابق",
      sales: "فروشات",
      quotations: "کوټیشن",
      settings: "تنظیمات",
      backup: "پشتیبان",
      userManagement: "مدیریت کاربران",
      logout: "خروج"
    },
    printing: {
      title: "چاپخانه",
      subtitle: "مدیریت کارهای چاپ بیرق و پرداخت ها",
      selectPrinter: "انتخاب چاپخانه",
      newPrinter: "ثبت چاپخانه جدید",
      printer: "چاپخانه",
      printerName: "نام چاپخانه",
      printerPhone: "شماره تماس",
      printerAddress: "آدرس",
      jobTitle: "کار چاپ",
      flagName: "نام بیرق",
      size: "سایز",
      qty: "تعداد",
      totalMeters: "مجموع اندازه (متر)",
      perMeterPrice: "قیمت فی متر",
      totalPrice: "قیمت مجموعی",
      makingUnitPrice: "قیمت ساخت (فی واحد)",
      sellingUnitPrice: "قیمت فروش (فی واحد)",
      lineSubtotal: "جمع ردیف",
      grandTotal: "مجموع کل",
      totalProfit: "مجموع سود",
      makingRateColumn: "ساخت / نرخ",
      afnPerMeter: "افغانی/متر",
      cash: "نقد",
      partial: "پرداخت قسمی",
      credit: "قرض",
      paymentAmount: "مبلغ پرداخت",
      reference: "حواله / ریفرنس",
      addLine: "افزودن ردیف",
      createRecord: "ثبت سفارش چاپ",
      records: "سوابق چاپ",
      billTitle: "بل چاپ",
      billNumber: "نمبر بل",
      printBill: "چاپ بل",
      viewBill: "مشاهده بل",
      payments: "پرداخت‌ها",
      paymentExceedsDue: "مبلغ پرداخت از باقی‌مانده بیشتر است",
      paymentSectionTitle: "پرداخت",
      paymentMethodLabel: "روش پرداخت",
      paymentHintCash: "تمام مبلغ همین حالا پرداخت می‌شود",
      paymentHintPartial: "قسمتی حالا پرداخت می‌شود، باقی بعداً",
      paymentHintCredit: "فعلاً پرداخت ندارد، بعداً پرداخت شود",
      paymentAmountPlaceholder: "مبلغ پرداخت فعلی را وارد کنید",
      amountPayingNow: "مبلغ پرداخت فعلی",
      remainingAfterPayment: "باقی‌مانده بعد از پرداخت",
      partialPaymentRequired: "مبلغ پرداخت قسمی باید بیشتر از صفر باشد",
      partialPaymentExceedsTotal: "پرداخت قسمی نمی‌تواند از مجموع بیشتر باشد",
      lineItemsHeading: "اقلام",
      lineProfit: "سود ردیف",
      perMeterPriceLegacy: "فی متر (قدیمی)",
      editPaymentsOnDetails: "پس از ذخیره، پرداخت‌ها را از صفحه پرداخت‌ها مدیریت کنید.",
      created: "سفارش چاپ با موفقیت ثبت شد",
      failedSave: "ثبت سفارش چاپ ناموفق بود",
      pressBillLinesTruncated: "فقط {{shown}} سطر اول از {{total}} سطر در این صفحه نمایش داده شد. مجموع پایین برای کل کار است.",
      lineNo: "نمبر"
    },
    rent: {
      title: "کرایه",
      subtitle: "مدیریت دکان‌ها و پرداخت‌های کرایه",
      shop: "دکان",
      shopNo: "نمبر دکان",
      tenant: "کرایه‌دار",
      addShop: "ثبت کرایه دکان",
      editShop: "ویرایش کرایه دکان",
      rentDate: "تاریخ کرایه",
      duration: "مدت",
      periodType: "نوع دوره",
      weekly: "هفتگی",
      monthly: "ماهانه",
      rentAmount: "مبلغ کرایه",
      totalPaid: "مجموع پرداخت",
      totalDue: "مجموع قابل پرداخت",
      remainingPeriods: "دوره‌های باقی‌مانده",
      addPayment: "ثبت پرداخت",
      amountPaid: "مبلغ پرداخت‌شده",
      paymentDate: "تاریخ پرداخت",
      ownerName: "مالک / موجر اصلی",
      ownerNamePlaceholder: "نام شخص یا شرکتی که کرایه به آن پرداخت می‌شود",
      searchPlaceholder: "جستجو: دکان، کرایه‌دار، مالک…",
      filterPeriod: "نوع دوره",
      allPeriods: "همه",
      deleteShop: "حذف کرایه دکان",
      deleteConfirm: "این سابقه کرایه حذف شود؟ تمام پرداخت‌های مرتبط نیز حذف می‌شوند.",
      backToList: "بازگشت به لیست",
      loadError: "بارگذاری سابقه کرایه ناموفق بود.",
      deleteError: "حذف انجام نشد.",
      paymentAdded: "پرداخت ثبت شد.",
      paymentAddError: "ثبت پرداخت ناموفق بود.",
      shopDeleted: "سابقه کرایه حذف شد.",
      printBill: "چاپ بل کرایه",
      printSlip: "چاپ رسید حرارتی",
      billTitle: "بل کرایه",
      thermalSlipTitle: "رسید پرداخت کرایه",
      paymentHistory: "تاریخچه پرداخت",
      editPayment: "ویرایش پرداخت",
      deletePaymentConfirm: "این پرداخت حذف شود؟",
      paymentUpdated: "پرداخت بروزرسانی شد.",
      paymentUpdateError: "بروزرسانی پرداخت ناموفق بود.",
      paymentDeleted: "پرداخت حذف شد.",
      paymentDeleteError: "حذف پرداخت ناموفق بود.",
      thankYou: "تشکر"
    },
    quotations: {
      title: "کوټیشن ها",
      subtitle: "مدیریت کوټیشن های پیشنهادی",
      createQuotation: "ایجاد کوټیشن",
      editQuotation: "ویرایش کوټیشن",
      newQuotation: "جدید",
      customer: "مشتری",
      searchCustomer: "جستجوی مشتری...",
      selectExisting: "انتخاب موجود",
      addNewCustomer: "اضافه نمودن مشتری جدید",
      enterCustomerName: "نام مشتری را وارد نمایید...",
      items: "اجناس",
      item: "جنس",
      itemName: "نام جنس",
      searchItem: "جستجوی جنس...",
      noItemsMatchSearch: "هیچ جنسی با جستجوی شما مطابقت ندارد.",
      itemSelected: "انتخاب شده: {{name}}",
      clearItemSearch: "پاک کردن",
      mode: "حالت",
      switchToSelect: "تغییر به انتخاب",
      switchToManual: "تغییر به دستی",
      flagSize: "اندازه بیرق",
      flagSizePlaceholder: "مثلاً، ۳×۵ فوت",
      designType: "نوع دیزاین",
      designTypePlaceholder: "مثلاً، گلدوزی شده",
      quantity: "تعداد",
      priceEstimate: "قیمت تخمینی (افغانی)",
      purchasePrice: "قیمت خرید (افغانی)",
      profitPercent: "مفاد (%)",
      profitPercen: "مفاد (%)",
      salePriceWithProfit: "قیمت فروش (با مفاد)",
      purchaseSubtotal: "جمع خرید",
      saleSubtotal: "جمع فروش",
      subtotal: "جمع فرعی",
      addItem: "+ اضافه نمودن جنس",
      remove: "حذف",
      notes: "یادداشت ها",
      create: "ایجاد",
      update: "بروزرسانی",
      quotationCreated: "کوټیشن با موفقیت ایجاد شد!",
      quotationUpdated: "کوټیشن با موفقیت بروزرسانی شد!",
      failedToLoad: "بارگذاری کوټیشن ناکام شد",
      quotationNotFound: "کوټیشن پیدا نشد",
      backToQuotations: "برگشت به کوټیشن ها",
      searchByCustomer: "جستجو بر اساس مشتری یا شناسه...",
      id: "شناسه",
      date: "تاریخ",
      amount: "مبلغ",
      actions: "عملکرد ها",
      noQuotationsFound: "کوټیشنی پیدا نشد",
      confirmDelete: "تأیید حذف",
      deleteMessage: "آیا مطمئن هستید که میخواهید کوټیشن برای",
      yesDelete: "بلی، حذف",
      createNewQuotation: "ایجاد کوټیشن پیشنهادی جدید",
      updateQuotationDetails: "بروزرسانی جزئیات کوټیشن",
      printHeading: "کوټیشن",
      printQuotation: "چاپ کوټیشن",
      billNoLabel: "نمبر بل",
      linesTruncated: "فقط {{shown}} قلم اول از {{total}} قلم در این صفحه نمایش داده شد.",
      quotationDateLabel: "تاریخ کوټیشن",
      quotationDateHelp: "اختیاری — اگر خالی باشد امروز ثبت می‌شود"
    },
    settings: {
      title: "تنظیمات سیستم",
      subtitle: "پیکربندی تنظیمات و گزینه‌های سیستم",
      systemIdentity: "هویت سیستم",
      systemName: "نام سیستم",
      systemLogo: "آدرس لوگوی سیستم",
      currencySettings: "تنظیمات ارز",
      primaryCurrency: "ارز اصلی",
      currencySymbol: "نماد ارز",
      regionalSettings: "تنظیمات منطقه‌ای",
      dateFormat: "فرمت تاریخ",
      timeFormat: "فرمت زمان",
      timezone: "منطقه زمانی",
      businessSettings: "تنظیمات تجاری",
      taxRate: "نرخ مالیات (%)",
      lowStockThreshold: "حد آستانه موجودی کم",
      contactInfo: "معلومات تماس",
      companyAddress: "آدرس شرکت",
      companyPhone: "تلفن شرکت",
      companyEmail: "ایمیل شرکت",
      saveSettings: "ذخیره تنظیمات",
      settingsSaved: "تنظیمات با موفقیت ذخیره شد",
      failedToSave: "ذخیره تنظیمات ناموفق بود",
      failedToLoad: "بارگذاری تنظیمات ناموفق بود",
      backupSection: "پشتیبان‌گیری از داده‌ها",
      backupIntro:
        "تمام جداول را در اکسل (یک شیت به ازای هر مدل، ستون‌های m2m_* برای ارتباط چند به چند و مسیر فایل‌های بارگذاری) یا فایل SQL کامل SQLite صادر کنید. بسته ZIP هم فایل‌های media/آپلودها را دارد. پشتیبان خودکار بر اساس گزینه‌های زیر روی سرور ذخیره می‌شود.",
      backupAutomatic: "فعال‌سازی پشتیبان خودکار",
      backupFrequency: "فاصلهٔ زمانی پشتیبان خودکار",
      backupDaily: "روزانه",
      backupWeekly: "هفتگی",
      backupMonthly: "ماهانه (حدود ۳۰ روز)",
      backupYearly: "سالانه (حدود ۳۶۵ روز)",
      backupIncludeExcel: "شامل اکسل در پشتیبان خودکار",
      backupIncludeSql: "شامل SQL در پشتیبان خودکار",
      backupLastAuto: "آخرین پشتیبان خودکار",
      backupManualHint:
        "دانلود دستی بلافاصله در مرورگر انجام می‌شود و به برنامهٔ زمانی بالا وابسته نیست.",
      backupExcel: "دانلود اکسل",
      backupSql: "دانلود SQL",
      backupBoth: "دانلود هر دو (ZIP)",
      backupDownloadStarted: "دانلود پشتیبان آغاز شد",
      backupDownloadFailed: "دانلود پشتیبان ناموفق بود",
      backupAutoLocation:
        "فایل‌های خودکار در سرور در مسیر media/backups/auto ذخیره می‌شوند (ZIP در صورت انتخاب هر دو نوع).",
      backupAuthLoading: "در حال بررسی حساب کاربری…",
      backupNeedsLogin: "برای پشتیبان‌گیری و دانلود وارد شوید.",
      backupNeedsAdmin:
        "پشتیبان‌گیری فقط برای مدیران (نقش admin) یا سوپریوزرهاست. از یک مدیر بخواهید نقش شما را تنظیم کند یا با حساب سوپریوزر Django وارد شوید.",
      backupJwtVsAdminNote:
        "ورود به Django admin (/admin/) فقط نشست همان سایت را فعال می‌کند. این برنامه از توکن JWT استفاده می‌کند: از صفحهٔ ورود همین برنامه با همان نام کاربری و رمز سوپریوزر وارد شوید. اگر «مرا به خاطر بسپار» خاموش باشد، توکن فقط در همین تب مرورگر است و اکنون برنامه آن را درست می‌خواند.",
      backupNeedJwtLogin:
        "در صفحهٔ ورود همین برنامه با نام کاربری و رمز Django (سوپریوزر هم می‌شود) وارد شوید. تنها باز کردن Django admin توکن را به این برنامه نمی‌فرستد.",
      backupPageTitle: "پشتیبان‌گیری و بازیابی",
      backupPageSubtitle: "پشتیبان کامل سیستم را بر اساس دوره بسازید و از پشتیبان قبلی بازیابی کنید.",
      backupOpenPage: "باز کردن صفحه پشتیبان",
      backupFullSection: "پشتیبان کامل سیستم",
      backupRangeSection: "پشتیبان بر اساس دوره",
      backupCreateTitle: "ایجاد پشتیبان",
      backupRestoreSection: "بازیابی پشتیبان",
      backupRestoreIntro: "یک فایل ZIP یا SQL از پشتیبان کامل قبلی را بازیابی کنید. این کار داده‌های فعلی دیتابیس را جایگزین می‌کند.",
      backupRestoreWarning: "بازیابی، داده‌های فعلی سیستم را بازنویسی می‌کند. فقط از فایل‌های پشتیبان کامل و قابل اعتماد استفاده کنید.",
      backupRangeIntro:
        "روزانه، هفتگی، ماهانه، سالانه یا برچسب دلخواه را انتخاب کنید. هر دانلود پشتیبان کامل همه داده‌های سیستم (همه بخش‌ها) است، نه بخشی از داده‌ها.",
      backupAlwaysFullBadge: "همیشه داده‌های کامل سیستم",
      backupFullDataNote:
        "دوره فقط نام فایل را مشخص می‌کند (مثل daily / weekly). اکسل، SQL و ZIP همیشه کل دیتابیس را شامل می‌شوند. ZIP فایل‌های آپلود شده را هم دارد.",
      backupCustomLabelNote:
        "تاریخ‌های دلخواه فقط در نام فایل ذخیره می‌شوند. پشتیبان همچنان همه داده‌های سیستم را دارد.",
      backupPreset: "دوره پشتیبان",
      backupPresetAll: "همه داده‌ها",
      backupCustomRange: "بازه دلخواه",
      backupDateFrom: "از تاریخ",
      backupDateTo: "تا تاریخ",
      backupRestoreChooseFile: "انتخاب فایل پشتیبان",
      backupRestoreButton: "بازیابی پشتیبان",
      backupRestoring: "در حال بازیابی پشتیبان...",
      backupRestoreSuccess: "پشتیبان با موفقیت بازیابی شد",
      backupRestoreFailed: "بازیابی پشتیبان ناموفق بود",
      backupRangeDownloadExcel: "دانلود اکسل",
      backupRangeDownloadZip: "دانلود ZIP",
      backupFilteredZipNote:
        "ZIP شامل اکسل + SQL + media است. فیلتر دوره داده‌ها را کم نمی‌کند — فقط پشتیبان را برچسب می‌زند.",
      backupCustomDateRequired: "برای بازه دلخواه، هر دو تاریخ شروع و پایان را انتخاب کنید.",
      backupExcelDesc: "همه جداول در یک فایل اکسل",
      backupSqlDesc: "فایل SQL کامل برای بازیابی",
      backupBothDesc: "اکسل + SQL + فایل‌های media (پیشنهادی)",
      backupRecommended: "بهتر",
      backupDownloadAction: "دانلود",    },
    userManagement: {
      title: "مدیریت کاربران",
      subtitle: "مدیریت کاربران سیستم و مجوزها",
      addUser: "افزودن کاربر",
      editUser: "ویرایش کاربر",
      username: "نام کاربری",
      email: "ایمیل",
      firstName: "نام",
      lastName: "نام خانوادگی",
      role: "نقش",
      admin: "مدیر",
      staff: "کارمند",
      password: "رمز عبور",
      confirmPassword: "تأیید رمز عبور",
      isActive: "فعال",
      createdAt: "تاریخ ایجاد",
      actions: "عملیات",
      edit: "ویرایش",
      delete: "حذف",
      cancel: "لغو",
      save: "ذخیره",
      deleteConfirm: "آیا مطمئن هستید که می‌خواهید این کاربر را حذف کنید؟",
      userAdded: "کاربر با موفقیت افزوده شد",
      userUpdated: "کاربر با موفقیت به‌روزرسانی شد",
      userDeleted: "کاربر با موفقیت حذف شد",
      failedToAdd: "افزودن کاربر ناموفق بود",
      failedToUpdate: "به‌روزرسانی کاربر ناموفق بود",
      failedToDelete: "حذف کاربر ناموفق بود",
      failedToLoad: "بارگذاری کاربران ناموفق بود",
      passwordMismatch: "رمزهای عبور مطابقت ندارند",
      fullAccess: "دسترسی کامل به سیستم",
      partialAccess: "دسترسی محدود بر اساس نقش"
    },
    rozNamcha: {
      title: "روز نامچه",
      addEntry: "افزودن ورودی روزنامه",
      itemName: "نام قلم",
      costPrice: "هزینه/قیمت",
      dailyTotal: "مجموع روزانه",
      weeklyTotal: "مجموع هفتگی",
      monthlyTotal: "مجموع ماهانه",
      today: "امروز",
      thisWeek: "این هفته",
      thisMonth: "این ماه",
      noEntriesFound: "ورودی یافت نشد",
      dailyReport: "گزارش روزانه روزنامه",
      weeklyReport: "گزارش هفتگی روزنامه",
      monthlyReport: "گزارش ماهانه روزنامه",
      totalEntries: "کل ورودی‌ها",
      credit: "اعتبار",
      debit: "برداشت",
      balance: "بیلانس",
      failedToFetch: "بارگذاری ورودی ناموفق بود",
      itemNameRequired: "نام قلم الزامی است",
      dateRequired: "تاریخ الزامی است",
      costPriceRequired: "هزینه/قیمت باید بزرگتر از صفر باشد",
      fixErrors: "لطفاً خطاهای فرم را برطرف کنید",
      entryUpdated: "ورودی با موفقیت به‌روزرسانی شد",
      entryCreated: "ورودی با موفقیت ایجاد شد",
      failedToSave: "ذخیره ورودی ناموفق بود",
      entryNotFound: "ورودی یافت نشد",
      entryDeleted: "ورودی با موفقیت حذف شد",
      failedToDelete: "حذف ورودی ناموفق بود",
      addNew: "افزودن ورودی روزنامه",
      editEntry: "ویرایش ورودی روزنامه",
      enterItemName: "نام قلم را وارد کنید",
      enterDescription: "توضیحات را وارد کنید (اختیاری)",
      saving: "در حال ذخیره...",
      updateEntry: "به‌روزرسانی ورودی",
      saveEntry: "ذخیره ورودی"
    },
    modals: {
      confirmDelete: "تأیید حذف",
      deleteMessage: "آیا مطمئن هستید که می‌خواهید حذف کنید",
      deleteEntryMessage: "آیا مطمئن هستید که می‌خواهید این ورودی را حذف کنید؟ این کار قابل بازگشت نیست.",
      yesDelete: "بله، حذف",
      keep: "نگاه دارید",
      confirm: "تأیید",
      cancel: "لغو"
    },
    pagination: {
      showing: "نمایش",
      to: "تا",
      of: "از",
      results: "نتیجه",
      perPage: "در صفحه",
      page: "صفحه",
      previous: "قبلی",
      next: "بعدی"
    },
    reports: {
      startDate: "تاریخ شروع",
      endDate: "تاریخ پایان"
    },
    dashboard: {
      title: "داشبورد",
      subtitle: "نمای کلی و تجزیه و تحلیل تجارت به‌صورت زنده",
      refresh: "تازه‌سازی",
      failedToLoad: "بارگذاری داده‌های داشبورد ناموفق بود",
      retry: "تلاش مجدد",
      filterLabel: "فلتر:",
      daily: "روزانه",
      weekly: "هفتگی",
      monthly: "ماهانه",
      yearly: "سالانه",
      dailyRevenue: "درآمد روزانه",
      weeklyRevenue: "درآمد هفتگی",
      monthlyRevenue: "درآمد ماهانه",
      dailyExpenses: "مصارفات روزانه",
      weeklyExpenses: "مصارفات هفتگی",
      monthlyExpenses: "مصارفات ماهانه",
      netProfit: "سود خالص",
      netBalance: "موجودی خالص",
      totalOrders: "کل سفارشات",
      customerDues: "بدهی مشتریان",
      supplierPayables: "بدهی به تأمین‌کنندگان",
      lowStockItems: "مواد کم موجودی",
      vsLastMonth: "در مقابل ماه قبل",
      thisMonth: "این ماه",
      today: "امروز",
      thisWeek: "این هفته",
      positive: "مثبت",
      negative: "منفی",
      pending: "در انتظار",
      critical: "بحرانی",
      fromCustomers: "از {{count}} مشتری",
      toSuppliers: "به {{count}} تأمین‌کننده",
      revenueVsExpensesTrend: "روند درآمد در مقابل مصارفات",
      financialOverview: "نمای کلی مالی",
      noTrendData: "داده روند موجود نیست",
      grossProfit: "سود ناخالص",
      realTimeTransactions: "تراکنش‌های زنده",
      lastUpdated: "آخرین به‌روزرسانی",
      type: "نوع",
      details: "جزئیات",
      amount: "مبلغ",
      date: "تاریخ",
      status: "وضعیت",
      noRecentTransactions: "تراکنش اخیری وجود ندارد",
      showing: "نمایش",
      to: "تا",
      of: "از",
      page: "صفحه",
      customersWithDues: "مشتریان با بدهی معوق",
      customer: "مشتری",
      dueAmount: "مبلغ بدهی",
      orders: "سفارشات",
      noOutstandingDues: "بدهی معوقی وجود ندارد",
      suppliersWithBalance: "تأمین‌کنندگان با موجودی معوق",
      supplier: "تأمین‌کننده",
      balance: "موجودی",
      purchases: "خریداری",
      noOutstandingBalances: "موجودی معوقی وجود ندارد",
      totalCustomers: "کل مشتریان",
      active: "فعال",
      totalInventoryValue: "ارزش کل موجودی",
      itemsInStock: "{{count}} قلم در موجودی",
      activeEmployees: "کارمندان فعال",
      monthlySalary: "حقوق ماهانه",
      order: "سفارش",
      purchase: "خریداری",
      expense: "مصارفات",
      roznamcha: "روزنامه",
      completed: "تکمیل شده",
      pending: "در انتظار",
      cancelled: "لغو شده",
      partial: "جزئی",
      due: "بدهی",
      revenue: "درآمد",
      expenses: "مصارفات",
      supplierPayment: "پرداخت به تأمین‌کننده",
      chartNoData: "داده نیست",
      pieRevenue: "درآمد",
      pieExpenses: "مصارفات",
      piePurchases: "خریداری",
      orderChartLabel: "سفارش {{id}}",
      statusPaid: "پرداخت‌شده",
      stockAvailable: "موجودی کافی است",
      insufficientStock: "موجودی کافی نیست",
      currentStock: "موجودی فعلی",
      requested: "درخواست‌شده",
      shortage: "کمبود",
      customRange: "بازه دلخواه",
      dateFrom: "از تاریخ",
      dateTo: "تا تاریخ",
      apply: "اعمال"
    },
    sales: {
      title: "فروش",
      subtitle: "فروش جدید ایجاد کنید و پرداخت را انجام دهید",
      viewOrders: "مشاهده سفارشات",
      saleCompleted: "فروش تکمیل شد!",
      printBill: "چاپ فیش",
      newSale: "فروش جدید",
      ordersCreated: "سفارشات ایجاد شده",
      customer: "مشتری",
      totalAmount: "مبلغ کل",
      status: "وضعیت",
      completed: "تکمیل شده",
      customerInformation: "معلومات مشتری",
      searchCustomer: "جستجوی مشتری *",
      searchPlaceholder: "بر اساس نام یا شماره تماس جستجو کنید...",
      noCustomersFound: "مشتری یافت نشد",
      name: "نام",
      phone: "شماره تماس",
      email: "ایمیل",
      address: "آدرس",
      saleItems: "اقلام فروش",
      addItem: "افزودن قلم",
      item: "قلم *",
      selectItem: "قلم را انتخاب کنید...",
      quantity: "مقدار *",
      qty: "تعداد",
      pricePerUnit: "قیمت/واحد (افغانی) *",
      totalAfn: "مجموع (افغانی)",
      qualityDesignType: "نوع کیفیت/طراحی",
      selectDesignType: "نوع طراحی را انتخاب کنید (اختیاری)",
      addNewDesignType: "افزودن نوع طراحی جدید",
      newDesignTypeName: "نام نوع طراحی جدید",
      stockSource: "منبع موجودی *",
      pressStock: "موجودی چاپخانه",
      homeStock: "موجودی خانه",
      available: "موجود",
      orderSummary: "خلاصه سفارش",
      items: "اقلام",
      subtotal: "جمع فرعی",
      total: "مجموع",
      proceedToPayment: "ادامه به پرداخت",
      resetForm: "بازنشانی فرم",
      processing: "در حال پردازش...",
      quickInfo: "معلومات سریع",
      totalCustomers: "کل مشتریان",
      availableItems: "اقلام موجود",
      itemsInCart: "اقلام در سبد",
      pleaseSelectCustomer: "لطفاً یک مشتری انتخاب کنید",
      pleaseSelectItem: "لطفاً یک قلم انتخاب کنید",
      quantityRequired: "مقدار باید بزرگتر از صفر باشد",
      insufficientStock: "موجودی کافی نیست. موجود: {{count}}",
      priceRequired: "قیمت باید بزرگتر از صفر باشد",
      fixErrors: "لطفاً قبل از ادامه، خطاها را برطرف کنید",
      atLeastOneItem: "حداقل یک قلم الزامی است",
      saleCompletedSuccess: "فروش با موفقیت تکمیل شد!",
      failedToFetchCustomers: "بارگذاری مشتریان ناموفق بود",
      failedToFetchItems: "بارگذاری موجودی ناموفق بود",
      designTypeAdded: "نوع طراحی با موفقیت اضافه شد",
      failedToAddDesignType: "افزودن نوع طراحی ناموفق بود",

      // فروش مستقیم (بدون موجودی گدام)
      directSalesTitle: "فروش‌های مستقیم",
      directSalesSubtitle: "اجناس از بیرون خریده شده و مستقیم به مشتری فروخته می‌شود",
      newDirectSale: "فروش مستقیم جدید",
      noDirectSalesFound: "هیچ فروش مستقیم یافت نشد",
      createFirstDirectSale: "اولین فروش مستقیم را ایجاد کنید",
      unpaid: "باقی‌دار",

      createDirectSaleTitle: "ایجاد فروش مستقیم",
      createDirectSaleSubtitle: "از منبع خارجی خرید کرده و مستقیم به مشتری بفروشید (بدون موجودی گدام)",
      itemsToSell: "اقلام برای فروش",
      customerNameRequiredLabel: "نام مشتری *",
      selectCustomerOptional: "انتخاب از لست (اختیاری)",
      selectCustomerPlaceholder: "مشتری را انتخاب کنید...",

      sellingPricePerUnit: "قیمت فروش فی واحد *",
      costPricePerUnit: "قیمت خرید فی واحد",
      supplierNameOptionalLabel: "نام عرضه‌کننده (اختیاری)",

      financialSummary: "خلاصه مالی",
      subtotalSelling: "جمع فرعی (قیمت فروش)",
      totalCostBuying: "مجموعه قیمت خرید",
      profit: "سود",

      createDirectSaleDraft: "ایجاد فروش مستقیم (مسوده)",
      confirmDirectSale: "تأیید فروش مستقیم",
      failedToCreateDirectSale: "ایجاد فروش مستقیم ناموفق بود",
      directSaleCreated: "فروش مستقیم با موفقیت ایجاد شد",
      directSaleUpdated: "فروش مستقیم با موفقیت به‌روز شد",
      directSaleConfirmed: "فروش مستقیم با موفقیت تأیید شد",

      directSaleDetailsTitle: "فروش مستقیم",
      saleInformation: "معلومات فروش",
      paymentStatus: "وضعیت پرداخت",
      saleDate: "تاریخ",
      itemsSectionTitle: "اقلام",
      notesSectionTitle: "یادداشت‌ها",
      paymentsSectionTitle: "پرداخت‌ها",
      addPayment: "افزودن پرداخت",
      saleNotFound: "فروش یافت نشد",
      backToDirectSales: "برگشت به فروش‌های مستقیم",
      amount: "مبلغ",
      paymentMethodLabel: "روش پرداخت",
      addPaymentButton: "افزودن پرداخت",
      failedToLoadDirectSales: "بارگذاری فروش‌های مستقیم ناموفق بود",
      failedToLoadDirectSaleDetails: "بارگذاری جزئیات فروش مستقیم ناموفق بود",
      paymentAddedSuccess: "پرداخت با موفقیت اضافه شد",
      failedToAddPayment: "افزودن پرداخت ناموفق بود",

      // فروش مستقیم
      directSale: "فروش مستقیم",
      saleInformation: "معلومات فروش",
      financialSummary: "خلاصه مالی",
      items: "اجناس",
      notes: "یادداشت ها",
      payments: "پرداخت ها",
      addPayment: "ثبت پرداخت",
      print: "چاپ",
      printSize: "اندازه چاپ",
      editAndAddItems: "ویرایش و اضافه نمودن اجناس",
      saleNotFound: "فروش پیدا نشد",
      backToDirectSales: "برگشت به فروش مستقیم",
      size: "اندازه",
      design: "دیزاین",
      supplier: "تامینکننده",
      cash: "نقد",
      card: "کارت",
      bankTransfer: "انتقال بانکی",
      check: "چک",
      printDirectSaleBill: "چاپ بل فروش مستقیم"
    },
    directSales: {
      title: "فروش مستقیم",
      subtitle: "مدیریت فروش مستقیم بدون موجودی",
      directSale: "فروش مستقیم",
      saleInformation: "معلومات فروش",
      financialSummary: "خلاصه مالی",
      totalAmount: "مبلغ کل",
      cost: "هزینه",
      discount: "تخفیف",
      netAmount: "مبلغ خالص",
      profit: "مفاد",
      items: "اجناس",
      notes: "یادداشت ها",
      payments: "پرداخت ها",
      addPayment: "ثبت پرداخت",
      print: "چاپ",
      printSize: "اندازه چاپ",
      editAndAddItems: "ویرایش و اضافه نمودن اجناس",
      saleNotFound: "فروش پیدا نشد",
      backToDirectSales: "برگشت به فروش مستقیم",
      status: "وضعیت",
      paymentStatus: "وضعیت پرداخت",
      date: "تاریخ",
      size: "اندازه",
      design: "دیزاین",
      supplier: "تامینکننده",
      qty: "تعداد",
      price: "قیمت",
      total: "مجموع",
      amount: "مبلغ",
      paymentMethod: "روش پرداخت",
      paymentAdded: "پرداخت با موفقیت ثبت شد",
      failedToAddPayment: "ثبت پرداخت ناموفق بود",
      failedToLoad: "بارگذاری جزئیات فروش ناموفق بود",
      cash: "نقد",
      card: "کارت",
      bankTransfer: "انتقال بانکی",
      check: "چک",
      printDirectSaleBill: "چاپ بل فروش مستقیم",
      cancel: "لغو",
      deletedSuccess: "فروش مستقیم حذف شد",
      deleteFailed: "حذف فروش مستقیم انجام نشد",
      deleteDirectSaleTitle: "حذف فروش مستقیم؟",
      deleteDirectSaleMessage: "این کار فروش مستقیم و پرداخت‌های آن را برای همیشه حذف می‌کند."
    },
    auth: {
      welcomeBack: "خوش آمدید",
      signInSubtitle: "به داشبورد خود وارد شوید (تست: admin/admin)",
      emailUsername: "ایمیل یا نام کاربری",
      emailUsernamePlaceholder: "ایمیل یا نام کاربری خود را وارد کنید",
      password: "رمز عبور",
      passwordPlaceholder: "رمز عبور خود را وارد کنید",
      rememberMe: "مرا به خاطر بسپار",
      forgotPassword: "رمز عبور را فراموش کرده‌اید؟",
      signIn: "ورود",
      signingIn: "در حال ورود...",
      createAccount: "ایجاد حساب",
      noAccount: "حساب کاربری ندارید؟",
      createAccountTitle: "ایجاد حساب",
      joinSystem: "به سیستم ما بپیوندید",
      username: "نام کاربری",
      usernamePlaceholder: "یک نام کاربری انتخاب کنید",
      emailAddress: "آدرس ایمیل",
      emailPlaceholder: "آدرس ایمیل خود را وارد کنید",
      confirmPassword: "تأیید رمز عبور",
      confirmPasswordPlaceholder: "رمز عبور خود را تأیید کنید",
      acceptTerms: "من قبول می‌کنم",
      termsAndConditions: "شرایط و ضوابط",
      and: "و",
      privacyPolicy: "سیاست حفظ حریم خصوصی",
      creatingAccount: "در حال ایجاد حساب...",
      alreadyHaveAccount: "قبلاً حساب دارید؟",
      signInHere: "اینجا وارد شوید",
      emailRequired: "ایمیل/نام کاربری الزامی است",
      passwordRequired: "رمز عبور الزامی است",
      loginFailed: "ورود ناموفق بود. دوباره تلاش کنید.",
      usernameRequired: "نام کاربری الزامی است",
      usernameMinLength: "نام کاربری حداقل ۳ حرف باشد",
      usernameInvalid: "نام کاربری فقط حروف، اعداد و خط زیر می‌تواند داشته باشد",
      emailInvalid: "لطفاً یک آدرس ایمیل معتبر وارد کنید",
      passwordMinLength: "رمز عبور حداقل ۸ حرف باشد",
      confirmPasswordRequired: "لطفاً رمز عبور را تأیید کنید",
      passwordsDontMatch: "رمزهای عبور مطابقت ندارند",
      fixValidationErrors: "لطفاً خطاهای اعتبارسنجی را برطرف کنید",
      acceptTermsRequired: "باید شرایط و ضوابط را بپذیرید",
      chooseStrongerPassword: "لطفاً یک رمز عبور قوی‌تر انتخاب کنید",
      registrationFailed: "ثبت نام ناموفق بود. دوباره تلاش کنید.",
      weak: "ضعیف",
      medium: "متوسط",
      strong: "قوی",
      atLeast8Chars: "حداقل ۸ حرف",
      lowercaseLetter: "حرف کوچک",
      uppercaseLetter: "حرف بزرگ",
      number: "عدد",
      specialChar: "کاراکتر خاص"
    },
    orders: {
      title: "سفارشات",
      subtitle: "مدیریت و پیگیری تمام سفارشات",
      newOrder: "سفارش جدید",
      filters: "فیلترها",
      status: "وضعیت",
      allStatus: "همه وضعیت‌ها",
      customer: "مشتری",
      searchCustomer: "جستجوی مشتری...",
      startDate: "تاریخ شروع",
      endDate: "تاریخ پایان",
      bulkActions: "سفارش انتخاب شده",
      cancelSelected: "لغو انتخاب‌شده",
      orderId: "شناسه سفارش",
      flagSize: "اندازه پرچم",
      flagStandTypeLabel: "نوع پایه بیرق",
      qualityDesignLabel: "کیفیت / نوع دیزاین",
      qty: "تعداد",
      total: "مجموع",
      due: "بدهی",
      actions: "اقدامات",
      itemsPerPage: "اقلام در صفحه",
      previous: "قبلی",
      next: "بعدی",
      page: "صفحه",
      exportCsv: "خروجی CSV",
      noOrdersFound: "سفارشی یافت نشد",
      cancelOrder: "لغو سفارش",
      completeOrder: "تکمیل سفارش",
      cancelMultiple: "لغو چند سفارش",
      cancelConfirm: "آیا مطمئن هستید که می‌خواهید این سفارش را لغو کنید؟",
      completeConfirm: "آیا این سفارش را تکمیل شده علامت بزنید؟",
      cancelSelectedConfirm: "سفارشات انتخاب‌شده لغو شوند؟",
      cancelPaymentConfirm: "این پرداخت از فرمایش حذف شود؟ مانده فرمایش به‌روز می‌شود.",
      cancelOrders: "لغو سفارش(ها)",
      keep: "نگاه دارید",
      orderCancelled: "سفارش با موفقیت لغو شد",
      orderCompleted: "سفارش تکمیل شده علامت زده شد",
      ordersCancelled: "سفارش با موفقیت لغو شدند",
      createOrderPageTitle: "ایجاد فرمایش جدید",
      createOrderPageSubtitle: "درخواست فرمایش مشتری - موجودی کاهش نمی‌یابد تا به فروش تبدیل شود",
      addItemButton: "افزودن قلم",
      convertToSale: "تبدیل به فروش",
      markReady: "علامت به عنوان آماده",
      markInProduction: "علامت در تولید",
      inProduction: "در تولید",
      ready: "آماده",
      delivered: "تحویل شده",
      partiallyDelivered: "نیمه‌تحویل",
      editOrderLoadFailed: "بارگذاری فرمایش برای تصحیح ناموفق بود.",

      productType: "نوع محصول",
      stockSource: "منبع موجودی *",
      enterQuantity: "مقدار را وارد کنید",
      enterPrice: "قیمت را وارد کنید",
      createOrderButton: "ایجاد فرمایش",
      creatingOrder: "در حال ایجاد فرمایش...",
      headerTotal: "مجموع",
      manualEntry: "ورود دستی",
      customerRequired: "مشتری *",
      supplierRequired: "تأمین‌کننده *",
      supplier: "تأمین‌کننده",
      searchSupplierPlaceholder: "جستجوی تأمین‌کننده با نام یا تلفن...",
      noSuppliersInSearch: "تأمین‌کننده پیدا نشد",
      weOwe: "بدهکاری ما",
      supplierOutstandingHint: "باقیمانده دفتر تأمین‌کننده (بدهکاری ما)",
      supplierOffsetHint: "با «تسویه از حساب تأمین‌کننده» مبلغ این فروش از بدهکاری ما کم می‌شود.",
      supplierOffsetPayNote: "این پرداخت باقیمانده دفتر تأمین‌کننده (بدهکاری ما) را کم می‌کند.",
      searchCustomerPlaceholder: "جستجو یا نام مشتری را بنویسید...",
      noCustomersInSearch: "مشتری پیدا نشد",
      fetchNoCustomers: "مشتری نیست. ابتدا مشتری اضافه کنید.",
      fetchCustomersFailed: "بارگذاری مشتریان ناموفق: {{message}}",
      fetchItemsFailed: "بارگذاری اجناس ناموفق: {{message}}",
      orderItemsSection: "اقلام فرمایش",
      itemNumber: "قلم {{n}}",
      notSelected: "انتخاب نشده",
      qtyAbbr: "تعداد",
      removeItem: "حذف",
      manualItemHint: "ورود دستی — از ګدام کسر نمی‌شود",
      itemNameManual: "نام قلم *",
      itemNameManualPh: "نام قلم را دستی وارد کنید...",
      loadingItems: "در حال بارگذاری اجناس...",
      noItemsUseManual: "جنس در ګدام نیست. با ورود دستی بدون موجودی اضافه کنید.",
      selectItem: "جنس را انتخاب کنید...",
      itemOptionStock: "{{name}} ({{sku}}) — چاپخانه: {{press}} | خانه: {{home}}",
      inventoryFilteredFallback:
        "تمام محصولات نهایی نشان داده می‌شود — برای لیست مشخص، در ګدام دسته «پایه بیرق» بسازید یا در نام/دسته «stand» بگذارید.",
      productTypeLabel: "نوع محصول *",
      productFlag: "بیرق",
      productFlagStand: "پایه بیرق",
      qualityDesignPh: "مثلاً پریمیوم، استاندارد",
      availableStock: "موجود: {{count}}",
      purchasePrice: "قیمت خرید",
      costPricePh: "قیمت تمام‌شده",
      sellingPrice: "قیمت فروش *",
      sellingPricePh: "قیمت فروش",
      lineTotal: "جمع:",
      checkingInventory: "بررسی موجودی...",
      totalAmountLabel: "مبلغ کل:",
      totalProfitLabel: "سود کل:",
      cancelButton: "لغو",
      customerDetailsTitle: "معلومات مشتری",
      notesLabel: "یادداشت:",
      orderSummaryTitle: "خلاصه فرمایش",
      pricePerUnitLabel: "قیمت فی واحد:",
      paymentModalTitle: "معلومات پرداخت",
      payLaterNote: "برای ثبت فرمایش بدون پول نقد، ۰ وارد کنید؛ پرداخت را بعداً ثبت کنید.",
      fullPayment: "پرداخت کامل",
      partialPaymentRadio: "پرداخت جزئی",
      paymentAmountLabel: "مبلغ پرداخت *",
      remainingPayment: "باقیمانده: {{amount}} افغانی",
      paymentMethodLabel: "روش پرداخت *",
      payMethodCash: "نقدی",
      payMethodCredit: "نسیه",
      payMethodPartial: "جزئی",
      payMethodSupplierOffset: "تسویه از حساب تأمین‌کننده",
      creating: "در حال ایجاد...",
      errCustomerRequired: "مشتری الزامی است",
      errCustomerName: "نام مشتری الزامی است",
      errCustomerPhone: "شماره تماس مشتری الزامی است",
      errAtLeastOneItem: "حداقل یک قلم لازم است",
      errItemSelect: "انتخاب قلم الزامی است",
      errManualName: "نام قلم الزامی است",
      errFlagSize: "اندازه بیرق الزامی است",
      errFlagStand: "نوع پایه بیرق الزامی است",
      errQuantityPositive: "تعداد باید بیشتر از صفر باشد",
      errSellingPrice: "قیمت فروش باید بیشتر از صفر باشد",
      errPaymentPositive: "مبلغ پرداخت باید بیشتر از صفر باشد",
      errPaymentNegative: "مبلغ پرداخت نمی‌تواند منفی باشد",
      errPaymentExceeds: "پرداخت نمی‌تواند از مبلغ کل بیشتر باشد",
      errCreateCustomer: "ایجاد مشتری ناموفق بود",
      recordOrderNoPayment: "ذخیره فرمایش بدون پرداخت",
      stateCreatedMessage: "فرمایش ثبت شد. بل آماده چاپ است.",
      editOrderPageTitle: "تصحیح فرمایش #{{id}}",
      editOrderPageSubtitle: "تفاصیل فرمایش را به‌روز کنید",
      selectStatus: "وضعیت را انتخاب کنید...",
      statusPending: "در انتظار",
      statusCompleted: "تکمیل‌شده",
      statusCancelled: "لغو شده",
      updateOrderButton: "به‌روزرسانی فرمایش",
      updatingOrder: "در حال به‌روزرسانی...",
      errStatusRequired: "وضعیت الزامی است",
      stateUpdatedMessage: "فرمایش با موفقیت به‌روزرسانی شد!",
      billDateNotSet: "—",
      billNotesHeading: "یادداشت",
      returnRefund: "مرجوع/استرداد",
      returnType: "نوع مرجوعی",
      fullReturn: "مرجوع کامل",
      partialReturn: "مرجوع قسمی",
      returnReasonPlaceholder: "دلیل مرجوع/استرداد را بنویسید...",
      processReturnRefund: "اجرای مرجوع/استرداد"
    },
    sales: {
      title: "فروشات",
      subtitle: "پردازش فروشات و کاهش موجودی",
      printBill: "چاپ بل",
      printQuotation: "چاپ قیمت",
      newSale: "فروش جدید",
      createSale: "ایجاد فروش",
      createSaleDraft: "ایجاد فروش (پیش‌نویس)",
      confirmSale: "تأیید فروش",
      saleId: "شناسه فروش",
      saleDate: "تاریخ فروش",
      customer: "مشتری",
      customerName: "نام مشتری",
      searchCustomer: "جستجوی مشتری",
      searchExistingCustomer: "جستجوی مشتری موجود",
      searchCustomerPlaceholder: "برای جستجوی مشتری تایپ کنید...",
      enterCustomerManually: "یا دستی وارد کنید (در صورت نبود مشتری جدید ساخته می‌شود)",
      typeCustomerName: "نام مشتری را تایپ کنید...",
      createCustomerWithDetails: "ایجاد مشتری جدید با جزئیات",
      selectCustomer: "انتخاب مشتری",
      saleItems: "اقلام فروش",
      addItem: "افزودن قلم",
      item: "قلم",
      selectItem: "انتخاب قلم",
      quantity: "تعداد",
      pricePerUnit: "قیمت فی واحد",
      stockType: "نوع موجودی",
      pressStock: "موجودی چاپخانه",
      homeStock: "موجودی خانه",
      flagSize: "اندازه پرچم",
      flagSizeExample: "مثال: ۳x۵ فوت",
      qualityDesignType: "نوع کیفیت/طراحی",
      qualityDesignExample: "مثال: ممتاز، معمولی",
      total: "مجموع",
      subtotal: "جمع فرعی",
      discount: "تخفیف",
      tax: "مالیات",
      netAmount: "مبلغ خالص",
      financial: "جزئیات مالی",
      status: "وضعیت",
      paymentStatus: "وضعیت پرداخت",
      draft: "پیش‌نویس",
      confirmed: "تأیید شده",
      cancelled: "لغو شده",
      paid: "پرداخت شده",
      unpaid: "پرداخت نشده",
      partial: "پرداخت جزئی",
      payment: "پرداخت",
      addPayment: "افزودن پرداخت",
      paymentMethod: "روش پرداخت",
      amountPaid: "مبلغ پرداختی",
      notes: "یادداشت‌ها",
      additionalNotesPlaceholder: "یادداشت اضافی...",
      totalPaid: "مجموع پرداختی",
      balanceDue: "مانده بدهی",
      saleCreated: "فروش با موفقیت ایجاد شد",
      saleConfirmed: "فروش تأیید و موجودی کاهش یافت",
      saleCancelled: "فروش با موفقیت لغو شد",
      paymentAdded: "پرداخت با موفقیت افزوده شد",
      failedToCreate: "ایجاد فروش ناموفق بود",
      failedToConfirm: "تأیید فروش ناموفق بود",
      failedToCancel: "لغو فروش ناموفق بود",
      failedToLoad: "بارگذاری فروشات ناموفق بود",
      confirmSaleMessage: "این عمل موجودی را از انبار کاهش می‌دهد. ادامه؟",
      cancelSaleMessage: "این عمل موجودی را به انبار برمی‌گرداند. ادامه؟",
      stockDeducted: "موجودی هنگام تأیید فروش کاهش می‌یابد",
      noStockDeduction: "در فروش‌های پیش‌نویس موجودی کاهش نمی‌یابد",

      detailsTitle: "فروش #{{id}}",
      saveSaleDate: "ذخیره تاریخ",
      saleDateHelp:
        "تاریخ روی بل را عوض کنید، یا خالی بگذارید و ذخیره کنید تا تاریخ بل خالی باشد (—).",
      saleDateUpdated: "تاریخ فروش به‌روز شد",
      saleDateUpdateFailed: "به‌روزرسانی تاریخ فروش ناموفق بود",
      itemSizeColumn: "اندازه",
      customerColumn: "مشتری",
      phoneColumn: "تماس",
      paymentColumn: "پرداخت",
      noPaymentsYet: "پرداختی نیست",
      paymentHistory: "تاریخچه پرداخت",
      amount: "مبلغ",
      paymentLine: "#{{id}}: {{amount}} ({{method}})",
      editBill: "تصحیح بل",
      editBillTooltipDraft: "تصحیح اقلام و جمع",
      editBillTooltipConfirmed: "تصحیح اقلام (فروش به مسوده برمی‌گردد)",
      totalsSubtotal: "جمع فرعی",
      totalsDiscount: "تخفیف",
      totalsTax: "مالیات",
      totalsNet: "خالص",
      totalsPaid: "پرداخت‌شده",
      totalsDue: "مانده",
      modalConfirmSaleTitle: "تأیید فروش",
      modalCancelSaleTitle: "لغو فروش",
      saleCancelConfirmBody:
        "آیا این فروش لغو شود؟ در صورت تأیید، موجودی به انبار برمی‌گردد (در صورت کاربرد).",
      cancelSaleAction: "لغو فروش",
      editPaymentTitle: "تصحیح پرداخت",
      editPaymentAmountLabel: "مبلغ *",
      editPaymentUpdateButton: "به‌روزرسانی پرداخت",
      paymentUpdatedSuccess: "پرداخت به‌روز شد",
      paymentUpdateFailed: "به‌روزرسانی پرداخت ناموفق بود. دوباره امتحان کنید.",
      paymentCancelledSuccess: "پرداخت حذف شد",
      paymentCancelFailed: "حذف پرداخت ناموفق بود. دوباره امتحان کنید.",
      cancelPaymentTitle: "حذف پرداخت",
      invalidPaymentAmount: "مبلغ پرداخت معتبر وارد کنید",
      paymentModalAmountToPay: "مبلغ برای پرداخت: {{amount}}",
      paymentModalBreakdown: "خالص: {{net}} − پرداخت‌شده: {{paid}} = مانده: {{due}}",
      paymentModalMaxPlaceholder: "حداکثر: {{amount}}",
      paymentModalBalanceDue: "مانده بدهی: {{amount}}",
      failedToAddPayment: "افزودن پرداخت ناموفق بود",

      editSaleTitle: "تصحیح فروش #{{id}}",
      editSaleCustomerLabel: "مشتری *",
      editSaleSelectCustomerPlaceholder: "مشتری را انتخاب کنید",
      editSaleItemsLabel: "اقلام *",
      editSaleQuantityPlaceholder: "تعداد",
      editSalePricePlaceholder: "قیمت",
      editSaleCancelledCannotEdit: "فروش لغوشده قابل تصحیح نیست",
      editSaleLoadFailed: "بارگذاری فروش ناموفق بود",
      editSaleUpdatedSuccess: "فروش ذخیره شد. وضعیت به مسوده برگردانده شد.",
      editSaleUpdateFailed: "به‌روزرسانی فروش ناموفق بود",
      editSaleSubmit: "به‌روزرسانی فروش",

      totalAmount: "مبلغ کل",
      profit: "سود",
      directSalesTitle: "فروش‌های مستقیم",
      directSalesSubtitle: "اجناس از بیرون خریده شده و مستقیم به مشتری فروخته می‌شود",
      newDirectSale: "فروش مستقیم جدید",
      noDirectSalesFound: "هیچ فروش مستقیم یافت نشد",
      createFirstDirectSale: "اولین فروش مستقیم را ایجاد کنید",

      // Create Direct Sale form (no stock)
      createDirectSaleTitle: "ایجاد فروش مستقیم",
      createDirectSaleSubtitle: "از منبع خارجی خرید کرده و مستقیم به مشتری بفروشید (بدون موجودی گدام)",
      itemsToSell: "اقلام برای فروش",
      customerNameRequiredLabel: "نام مشتری *",
      selectCustomerOptional: "انتخاب از لست (اختیاری)",
      selectCustomerPlaceholder: "مشتری را انتخاب کنید...",
      sellingPricePerUnit: "قیمت فروش فی واحد *",
      purchaseCostPerUnit: "بهای خرید / فی واحد",
      costNotSetShort: "ثبت نشده",
      costNotSetInInventory:
        "بهای خرید برای این قلم در موجودی ثبت نشده است. در بخش موجودی، قیمت خرید (Cost price) را اضافه کنید تا سود ردیف درست نمایش داده شود.",
      lineProfitAtYourPrice: "سود این ردیف با قیمت فروش فعلی:",
      lineProfitHint: "تعداد × (قیمت فروش − بهای خرید)",
      profitIfSoldAtCost: "اگر به بهای خرید بفروشید (افغانی {{cost}} / فی واحد)، سود این ردیف:",
      forThisLineQty: "(تعداد {{qty}})",
      totalPurchaseCost: "جمع بهای خرید",
      profitSummaryFootnote:
        "سود = مبلغ خالص − جمع بهای خرید. ابتدا قیمت خرید روی قلم در موجودی؛ در غیر این صورت آخرین قیمت فی واحد از خرید چاپخانه برای همان قلم. اگر هیچ‌کدام نباشد در جمع بهای خرید لحاظ نمی‌شود.",
      listPriceReference: "قیمت لست",
      purchaseCostSourceInventory: "منبع: قیمت خرید ذخیره‌شده روی قلم موجودی.",
      purchaseCostSourceSupplier: "منبع: آخرین قیمت فی واحد از خرید تأمین‌کننده (چاپخانه) برای این قلم.",
      purchaseCostNoCostListPriceHint: "بهای خرید ثبت نشده — قیمت فروش/لست موجودی فقط برای معلومات نمایش داده می‌شود.",
      lineProfitNeedsCostOrUseList:
        "برای محاسبه سود، قیمت خرید را روی قلم بگذارید یا خرید چاپخانه با این قلم ثبت کنید. قیمت لست: افغانی {{list}} / فی واحد (به‌عنوان بهای خرید استفاده نمی‌شود).",

      selectOrEnterCustomer: "مشتری را از لست انتخاب کنید یا نام را تایپ کنید",
      failedToLoadOrderData: "بارگذاری داده‌های فرمایش برای این فروش ناموفق بود",
      quantityMustBeGreaterThanZero: "تعداد باید بیشتر از صفر باشد",
      priceMustBeGreaterThanZero: "قیمت فروش باید بیشتر از صفر باشد",
      itemNumber: "قلم {{n}}",
      insufficientStockForItem:
        "{{stockType}} کافی نیست (ردیف {{itemNumber}}). موجود: {{available}}.",
      apiErrorInsufficientStockForItem:
        "برای «{{itemName}}» {{stockType}} کافی نیست. موجود: {{available}}، درخواست: {{required}}.",
      apiErrorInsufficientStockSimple: "{{stockType}} کافی نیست. موجود: {{available}}.",
      apiErrorInsufficientStockAdjustment: "برای این تعدیل موجودی کافی نیست.",
      apiErrorFailedToRevertStock:
        "هنگام ذخیرهٔ فروش، برگرداندن موجودی انجام نشد. دوباره تلاش کنید یا با پشتیبانی تماس بگیرید.",
      apiErrorSaleAlreadyConfirmed: "این فروش قبلاً تأیید شده است.",
      apiErrorCannotConfirmCancelledSale: "فروش لغوشده را نمی‌توان تأیید کرد.",
      apiErrorSaleAlreadyCancelled: "این فروش قبلاً لغو شده است.",
      apiErrorDirectSaleAlreadyConfirmed: "این فروش مستقیم قبلاً تأیید شده است.",
      apiErrorPaymentMustBePositive: "مبلغ پرداخت باید بیشتر از صفر باشد.",
      costPriceNonNegative: "قیمت خرید (بهای تمام‌شده) نمی‌تواند منفی باشد.",
      totalStockLabel: "موجودی کل",
      stockLabelGeneric: "موجودی",
      quantityRequired: "تعداد باید بیشتر از صفر باشد",
      priceRequired: "قیمت فروش باید بیشتر از صفر باشد",
      directSaleUpdatedAndConfirmed: "فروش مستقیم به‌روز و تأیید شد.",
      availableStock: "موجودی",
      customerNameRequired: "نام مشتری الزامی است",
      customerCreatedSuccessfully: "مشتری با موفقیت ایجاد شد",
      failedToCreateCustomer: "ایجاد مشتری ناموفق بود",
      createNewCustomer: "مشتری جدید",
      enterCustomerName: "نام مشتری",
      enterPhoneNumber: "شماره تماس",
      enterAddress: "آدرس",
      createCustomer: "ایجاد مشتری",

      costPricePerUnit: "قیمت خرید فی واحد",
      supplierNameOptionalLabel: "نام عرضه‌کننده (اختیاری)",
      financialSummary: "خلاصه مالی",
      subtotalSelling: "جمع فرعی (قیمت فروش)",
      totalCostBuying: "مجموعه قیمت خرید",
      createDirectSaleDraft: "ایجاد فروش مستقیم (مسوده)",
      confirmDirectSale: "تأیید فروش مستقیم",
      failedToCreateDirectSale: "ایجاد فروش مستقیم ناموفق بود",
      failedToLoadDirectSales: "بارگذاری فروش‌های مستقیم ناموفق بود",
      failedToFetchCustomers: "بارگذاری مشتریان ناموفق بود",
      directSaleCreated: "فروش مستقیم با موفقیت ایجاد شد",
      directSaleUpdated: "فروش مستقیم با موفقیت بهروز شد",
      directSaleConfirmed: "فروش مستقیم با موفقیت تأیید شد",
      editDirectSaleTitle: "ویرایش فروش مستقیم",
      editDirectSaleSubtitle: "بهروزرسانی جزئیات فروش مستقیم",
      updateDirectSale: "بهروزرسانی فروش مستقیم",
      updateAndConfirm: "بهروزرسانی و تأیید",
      customerInformation: "معلومات مشتری"
    },
    bank: {
      title: "بانک",
      subtitle: "مدیریت واریز و برداشت پول",
      currentBalance: "بیلانس فعلی",
      totalDeposits: "مجموع واریزها",
      totalWithdraws: "مجموع برداشت‌ها",
      availableIncome: "درآمد قابل واریز",
      newTransaction: "تراکنش جدید",
      depositIncome: "واریز درآمد",
      deposit: "واریز",
      withdraw: "برداشت",
      credit: "اعتبار",
      debit: "بدهی",
      manual: "دستی",
      income: "درآمد",
      amount: "مبلغ",
      type: "نوع",
      source: "منبع",
      note: "یادداشت",
      transactions: "تراکنش‌ها",
      noTransactions: "تراکنشی موجود نیست",
      failedToLoad: "بارگذاری معلومات بانک ناموفق بود",
      amountRequired: "مبلغ باید بیشتر از صفر باشد",
      failedToSave: "ذخیره تراکنش ناموفق بود",
      depositSuccess: "واریز با موفقیت ثبت شد",
      withdrawSuccess: "برداشت با موفقیت ثبت شد",
      updateSuccess: "تراکنش با موفقیت به‌روزرسانی شد",
      deleteSuccess: "تراکنش با موفقیت حذف شد",
      failedToDelete: "حذف تراکنش ناموفق بود",
      incomeDepositSuccess: "درآمد با موفقیت واریز شد",
      noIncomeAvailable: "درآمدی برای واریز موجود نیست"
    },
    customers: {
      title: "مشتریان",
      subtitle: "مدیریت معلومات مشتریان",
      addCustomer: "افزودن مشتری",
      searchPlaceholder: "جستجو بر اساس نام یا شماره تماس...",
      searchPlaceholderSerial: "جستجو بر اساس نام، تماس یا شماره سریال...",
      manualSerialNo: "شماره سریال",
      manualSerialNoPlaceholder: "شماره دفتر / مرجع (اختیاری)",
      show: "نمایش",
      perPage: "در صفحه",
      noCustomersFound: "مشتری یافت نشد",
      name: "نام",
      phone: "شماره تماس",
      address: "آدرس",
      email: "ایمیل",
      date: "تاریخ",
      actions: "اقدامات",
      view: "مشاهده",
      ledgerLabel: "دفتر حساب",
      edit: "ویرایش",
      delete: "حذف",
      noEmail: "ایمیل ندارد",
      deletedSuccess: "با موفقیت حذف شد",
      failedToDelete: "حذف مشتری ناموفق بود",
      notFound: "مشتری یافت نشد",
      editCustomer: "ویرایش مشتری",
      addCustomerForm: "افزودن مشتری",
      updateInfo: "به‌روزرسانی معلومات مشتری",
      createNew: "ایجاد مشتری جدید",
      nameRequired: "نام الزامی است",
      phoneRequired: "شماره تماس الزامی است",
      addressRequired: "آدرس الزامی است",
      invalidEmail: "فرمت ایمیل نامعتبر است",
      enterName: "نام مشتری را وارد کنید",
      enterPhone: "شماره تماس را وارد کنید",
      enterEmailOptional: "آدرس ایمیل را وارد کنید (اختیاری)",
      enterAddress: "آدرس مشتری را وارد کنید",
      previousBalance: "باقی‌مانده قبلی",
      previousBalanceRemaining: "باقی‌مانده قابل پرداخت",
      noPreviousBalance: "برای این مشتری هیچ باقی‌مانده قبلی موجود نیست.",
      enterValidAmount: "لطفاً مبلغ معتبر وارد کنید.",
      previousBalancePaid: "پرداخت باقی‌مانده قبلی با موفقیت ثبت شد.",
      amountPlaceholder: "مبلغ",
      payingPreviousBalance: "در حال پرداخت...",
      payPreviousBalance: "پرداخت باقی‌مانده قبلی",
      editRemainingPrevBalanceTitle: "ویرایش مانده باقی‌مانده قبلی",
      editRemainingPrevBalanceLabel: "مبلغ باقی‌مانده بدهی (افغانی) *",
      editRemainingPrevBalanceHelp:
        "فقط مبلغی که هنوز بدهی است (عدد بزرگ) را ویرایش می‌کنید. جمع بیلانس اولیه دوباره محاسبه می‌شود: جمع پرداخت شده + این مانده — خط «اصلی» به‌روز می‌شود.",
      prevBalanceRemainingUpdated: "بیلانس قبلی بر اساس مانده واردشده به‌روز شد.",
      prevBalancePaymentDeletedRestored:
        "این پرداخت باقی‌مانده قبلی حذف شد. این مبلغ دوباره به بدهی باقی‌مانده قبلی برمی‌گردد.",
      hasDue: "بدهی معوق دارد",
      notes: "یادداشت‌ها",
      notesOptional: "یادداشت اضافی (اختیاری)",
      saving: "در حال ذخیره...",
      updateCustomer: "به‌روزرسانی مشتری",
      createCustomer: "ایجاد مشتری",
      ledger: {
        failedToFetch: "بارگذاری معلومات مشتری ناموفق بود",
        selectOrder: "لطفاً حداقل یک سفارش انتخاب کنید",
        validAmount: "لطفاً مبلغ پرداخت معتبر وارد کنید",
        amountExceeds: "مبلغ پرداخت (افغانی {{amount}}) از مجموع بدهی (افغانی {{total}}) بیشتر است",
        paymentSuccess: "پرداخت برای {{count}} سفارش با موفقیت ثبت شد",
        paymentFailed: "ثبت پرداخت ناموفق بود",
        notFound: "مشتری یافت نشد",
        exportPdf: "خروجی PDF",
        exportPdfTitle: "خروجی به PDF",
        recordPayment: "ثبت پرداخت",
        totalOrders: "کل سفارشات",
        totalPaid: "کل پرداخت شده",
        totalDue: "کل بدهی",
        totalBilled: "مجموع صورت‌حساب (سفارشات و فروشات)",
        activityHistory: "سفارشات و فروشات",
        ref: "مرجع",
        entryType: "نوع",
        typeOrder: "سفارش",
        typeSale: "فروش",
        typeDirectSale: "فروش مستقیم",
        itemsShort: "قلم",
        viewSale: "مشاهده فروش",
        viewDirectSale: "مشاهده فروش مستقیم",
        paymentLine: "پرداخت #{{n}}",
        paymentKind: "پرداخت",
        noActivity: "هنوز سفارش یا فروشی برای این مشتری ثبت نشده است.",
        orderHistory: "تاریخچه سفارشات",
        orderId: "شناسه سفارش",
        items: "اقلام",
        paid: "پرداخت شده",
        lastPayment: "آخرین پرداخت",
        viewOrder: "مشاهده سفارش",
        printBill: "چاپ بل",
        cancelOrder: "لغو سفارش",
        showing: "نمایش",
        to: "تا",
        of: "از",
        paymentMode: "نوع پرداخت",
        fullPayment: "پرداخت کامل",
        partialPayment: "پرداخت جزئی",
        selectOrders: "انتخاب سفارشات",
        deselectAll: "لغو انتخاب همه",
        selectAll: "انتخاب همه",
        noOrdersAvailable: "سفارشی موجود نیست",
        ordersSelected: "{{count}} سفارش انتخاب شده",
        selectInvoiceEntry: "لطفاً حداقل یک سفارش یا فروش با مانده بدهی انتخاب کنید.",
        paymentSuccessInvoices: "پرداخت برای {{count}} فاکتور با موفقیت ثبت شد.",
        paymentReceiptMultiple: "پرداخت برای {{count}} فاکتور",
        selectInvoices: "انتخاب فاکتورها (سفارش و فروش)",
        noOutstandingInvoices: "سفارش یا فروشی با مانده بدهی موجود نیست.",
        invoicesSelected: "{{count}} فاکتور انتخاب شده",
        total: "مجموع",
        totalDueLabel: "مجموع بدهی",
        amountLabel: "مبلغ",
        autoCalculated: "(محاسبه خودکار)",
        max: "حداکثر",
        paymentMethod: "روش پرداخت",
        reference: "مرجع",
        optional: "اختیاری",
        processing: "در حال پردازش...",
        printBillTitle: "چاپ بل",
        cancelOrderConfirm: "آیا مطمئن هستید که می‌خواهید سفارش #{{id}} را لغو کنید؟ این کار قابل بازگشت نیست و اقلام به موجودی برمی‌گردند.",
        keepOrder: "نگاه داشتن سفارش",
        cancelling: "در حال لغو...",
        pdfPluginError: "افزونه PDF بارگذاری نشده. لطفاً صفحه را مجدداً بارگذاری کنید.",
        loadOrderError: "بارگذاری جزئیات سفارش ناموفق بود",
        editPrevBalancePaymentHint:
          "فقط مبلغ همین خط پرداخت را وارد کنید. با کم کردن آن، مانده باقی‌مانده قبلی مشتری به همان میزان زیاد می‌شود (بخش برگشتی به بدهی قبلی برمی‌گردد).",
        editPrevBalancePaymentAmountLabel: "مبلغ این پرداخت (افغانی)",
        afterEditRemainingPrevBalance: "تخمین مانده باقی‌مانده قبلی پس از ذخیره",
        perPage: "در صفحه",
        prevBalancePaymentsSection: "پرداخت‌های باقی‌مانده قبلی",
        prevBalancePaymentsSectionHint: "پرداخت‌های ثبت‌شده نسبت به بیلانس اولیه (۵ در صفحه).",
        noPrevBalancePayments: "هنوز پرداخت باقی‌مانده قبلی ثبت نشده است.",
        paidTowardPrevBalance: "پرداخت‌شده نسبت به باقی‌مانده قبلی",
        openingTotalOnFile: "جمع بیلانس اولیه در پرونده",
        addPreviousBalanceShort: "افزودن باقی‌مانده قبلی",
        editOrderPaymentTitle: "ویرایش پرداخت سفارش",
        editSalePaymentTitle: "ویرایش پرداخت فروش",
        editDirectSalePaymentTitle: "ویرایش پرداخت فروش مستقیم",
        balancePaymentUpdated: "پرداخت باقی‌مانده قبلی به‌روز شد.",
        balancePaymentUpdateFailed: "به‌روزرسانی پرداخت باقی‌مانده قبلی ناموفق بود.",
        balancePaymentDeleteFailed: "حذف پرداخت باقی‌مانده قبلی ناموفق بود.",
        paymentUpdatedSuccess: "پرداخت با موفقیت به‌روز شد.",
        paymentUpdateFailed: "به‌روزرسانی پرداخت ناموفق بود.",
        paymentDeletedSuccess: "پرداخت با موفقیت حذف شد.",
        paymentDeleteFailed: "حذف پرداخت ناموفق بود.",
        deletePaymentTitle: "حذف پرداخت",
        deleteOrderPaymentConfirm:
          "این پرداخت از سفارش حذف شود؟ مانده سفارش به‌روز می‌شود.",
        deleteSalePaymentConfirm:
          "این پرداخت از فروش حذف شود؟ مانده فروش به‌روز می‌شود.",
        deleteDirectSalePaymentConfirm:
          "این پرداخت از فروش مستقیم حذف شود؟ مانده به‌روز می‌شود.",
        deletePrevBalancePaymentConfirm:
          "این پرداخت باقی‌مانده قبلی حذف شود؟ مانده باقی‌مانده قبلی مشتری تعدیل می‌شود."
      }
    },
    inventory: {
      title: "موجودی",
      addItem: "افزودن قلم",
      searchPlaceholder: "جستجو بر اساس نام یا کد...",
      allTypes: "همه انواع",
      rawMaterial: "مواد خام",
      finishedProduct: "محصول نهایی",
      noItemsFound: "قلمی یافت نشد",
      name: "نام",
      sku: "کد",
      type: "نوع",
      stock: "موجودی",
      minStock: "حداقل موجودی",
      price: "قیمت",
      status: "وضعیت",
      actions: "اقدامات",
      lowStock: "موجودی کم",
      inStock: "موجود",
      addItemPageTitle: "افزودن قلم",
      editItemPageTitle: "ویرایش قلم",
      addItemPageSubtitle: "ایجاد قلم جدید در ګدام",
      editItemPageSubtitle: "به‌روزرسانی قلم ګدام",
      failedToFetch: "بارگذاری موجودی ناموفق بود",
      failedToDelete: "حذف قلم ناموفق بود",
      formHeaderCreate: "افزودن قلم — ثبت جنس جدید در ګدام",
      formItemNameLabel: "نام قلم *",
      formItemNamePlaceholder: "نام قلم را وارد کنید",
      formTypeLabel: "نوع *",
      formCategoryLabel: "کتگوری *",
      formSelectCategory: "کتگوری را انتخاب کنید...",
      formAddCategoryTitle: "کتگوری جدید",
      formNewCategoryPlaceholder: "نام کتگوری جدید",
      formUnitPriceLabel: "قیمت فروش (فی واحد)",
      formUnitPricePlaceholder: "قیمت فروش را وارد کنید",
      purchasePriceLabel: "قیمت خرید (فی واحد)",
      profitPerUnitLabel: "مفاد فی واحد",
      profitPerUnitHint: "فروش منهای خرید",
      formFlagDesignLabel: "نوع دیزاین بیرق",
      formSelectDesignOptional: "نوع دیزاین (اختیاری)",
      formAddDesignTitle: "نوع دیزاین جدید",
      formNewDesignPlaceholder: "نام نوع دیزاین",
      formPressStockLabel: "موجودی چاپخانه *",
      formPressStockPlaceholder: "مقدار موجودی چاپخانه",
      formHomeStockLabel: "موجودی خانه *",
      formHomeStockPlaceholder: "مقدار موجودی خانه",
      formMinStockLabel: "حداقل موجودی *",
      formMinStockPlaceholder: "حداقل موجودی",
      formDescriptionLabel: "توضیحات",
      formDescriptionPlaceholder: "توضیحات (اختیاری)",
      formSkuNote: "با ذخیره، کد کالا (SKU) به‌صورت خودکار ساخته می‌شود.",
      formCreateButton: "ثبت قلم",
      formUpdateButton: "بروزرسانی قلم",
      errNameRequired: "نام قلم الزامی است",
      errCategoryRequired: "کتگوری الزامی است",
      errPressStock: "موجودی چاپخانه باید ۰ یا بیشتر باشد",
      errHomeStock: "موجودی خانه باید ۰ یا بیشتر باشد",
      errMinStock: "حداقل موجودی باید ۰ یا بیشتر باشد",
      toastCategoryAdded: "کتگوری اضافه شد",
      toastCategoryAddFailed: "اضافه کردن کتگوری ناموفق بود",
      toastDesignAdded: "نوع دیزاین اضافه شد",
      toastDesignAddFailed: "اضافه کردن نوع دیزاین ناموفق بود",
      toastFetchItemFailed: "بارگذاری جزئیات قلم ناموفق بود",
      toastItemUpdated: "قلم بروزرسانی شد",
      toastItemCreated: "قلم ثبت شد",
      toastSaveFailed: "ذخیره قلم ناموفق بود",
      detailsPageSubtitle: "جزئیات قلم",
      detailsNotFound: "قلم پیدا نشد",
      detailsBackToList: "بازگشت به ګدام",
      detailsEdit: "ویرایش",
      detailsDelete: "حذف",
      detailsDeleteConfirm: "آیا از حذف این قلم مطمئن هستید؟",
      toastItemDeleted: "قلم با موفقیت حذف شد",
      toastItemDeleteFailed: "حذف قلم ناموفق بود",
      detailsSectionInfo: "معلومات قلم",
      detailsSectionDescription: "توضیحات",
      detailsTableId: "شماره",
      detailsTableCategory: "کتگوری",
      detailsTableUnitPrice: "قیمت واحد",
      detailsTableCurrentStock: "موجودی فعلی",
      detailsTableMinStock: "حداقل موجودی",
      detailsTableCreated: "تاریخ ایجاد",
      detailsTableUpdated: "تاریخ بروزرسانی"
    },
    purchases: {
      title: "خریداري",
      addPurchase: "افزودن خریداري",
      item: "قلم",
      purchaseDate: "تاریخ خریداری",
      searchPlaceholder: "جستجو بر اساس قلم یا تأمین‌کننده...",
      noPurchasesFound: "خریداری یافت نشد",
      supplier: "تأمین‌کننده",
      itemName: "نام قلم",
      cost: "هزینه",
      date: "تاریخ",
      paymentStatus: "وضعیت پرداخت",
      allStatus: "همه وضعیت‌ها",
      paid: "پرداخت شده",
      partial: "جزئی",
      due: "بدهی",
      addPurchasePageTitle: "افزودن خریداري",
      editPurchasePageTitle: "ویرایش خریداري",
      addPurchasePageSubtitle: "ایجاد خریداري جدید",
      editPurchasePageSubtitle: "به‌روزرسانی معلومات خریداري",
      failedToFetch: "بارگذاری خریداری ناموفق بود",
      failedToDelete: "حذف خریداري ناموفق بود",
      quantity: "مقدار",
      paidAmount: "پرداخت شده",
      remaining: "باقیمانده",

      // فورم ایجاد خریداری – لیبل‌ها و Placeholder ها
      formSupplierLabel: "تأمین‌کننده *",
      formSelectExistingSupplier: "تأمین‌کننده موجود را انتخاب کنید...",
      formOrLabel: "یا",
      formTypeNewSupplier: "نام تأمین‌کننده جدید را بنویسید...",

      formItemLabel: "قلم",
      formSelectExistingItem: "قلم موجود را انتخاب کنید...",
      formOrTypeManually: "یا در قسمت پایین به‌صورت دستی بنویسید",
      formItemNameLabel: "نام قلم *",
      formItemNamePlaceholder: "نام قلم را وارد کنید",

      formQuantityWithUnitLabel: "مقدار (با واحد) *",
      formQuantityExamples: "مثلاً: ۱۰ متر، ۵٫۵ کیلو، ۱۰۰ عدد",

      formCostPerItemLabel: "قیمت فی قلم *",
      formCostPerItemPlaceholder: "قیمت هر قلم را وارد کنید",

      formForPressLabel: "این برای چاپخانه (تولید) است",
      splitStockLabel: "تقسیم موجودی (باید با مقدار کل برابر باشد: {{total}}) *",
      split5050: "تقسیم ۵۰ / ۵۰",
      splitHelpText: "مشخص کنید چه مقدار به موجودی چاپخانه و چه مقدار به موجودی خانه برود (مثلاً ۲۵ + ۲۵ = ۵۰).",
      formForPressWarning: "برای به‌روزرسانی موجودی گدام، لطفاً حتماً یک قلم را از لست بالایی انتخاب کنید.",

      formDescriptionLabel: "توضیحات",
      formDescriptionPlaceholder: "توضیحات را بنویسید (اختیاری)",
      formDescriptionPlaceholderPersonal: "توضیحات را بنویسید (برای خرید شخصی الزامی است)",

      formPaymentMethodLabel: "روش پرداخت",
      formCashLabel: "نقدی",

      formReferenceNumberLabel: "شماره مرجع",
      formReferenceNumberPlaceholder: "شماره مرجع را وارد کنید (اختیاری)",

      formCreateButton: "ایجاد کردن",
      formCancelButton: "لغو",
      formBillNumberLabel: "نمبر بل",
      formBillNumberPlaceholder: "نمبر بل عرضه‌کننده",
      formSelectItemOptional: "قلم را انتخاب کنید (اختیاری)...",
      formCalculation: "محاسبه:",
      formTotalCost: "مجموع هزینه: {{amount}}",
      formQtyTimesCost: "{{qty}} × {{cost}} = {{total}}",
      formSplitBetweenStocks: "تقسیم مقدار بین موجودی خانه و چاپخانه",
      formHomeQtyLabel: "مقدار موجودی خانه *",
      formPressQtyLabel: "مقدار موجودی چاپخانه *",
      formHomeQtyPlaceholder: "مقدار خانه",
      formPressQtyPlaceholder: "مقدار چاپخانه",
      formDistribution: "تقسیم:",
      formStockSelectHint: "مشخص کنید این خرید به کدام موجودی اضافه شود",
      formStockTypeLabel: "نوع موجودی *",
      formPressCheckboxPersonal: "برای به‌روزرسانی ګدام، «برای چاپخانه» را فعال کنید.",
      formDescriptionPersonalRequired: "برای خرید شخصی، توضیحات الزامی است",
      formPaymentCard: "کارت",
      formPaymentBank: "حواله بانکی",
      formPaymentCheck: "چک",
      formUpdatePurchase: "بروزرسانی خریداری",
      formCreatePurchase: "ثبت خریداری",
      errSupplierRequired: "تأمین‌کننده الزامی است",
      errItemNameRequired: "نام قلم الزامی است",
      errQuantityRequired: "مقدار الزامی است",
      errHomeQtyNegative: "مقدار خانه منفی نمی‌تواند باشد",
      errPressQtyNegative: "مقدار چاپخانه منفی نمی‌تواند باشد",
      errSplitMismatch: "خانه ({{home}}) + چاپخانه ({{press}}) باید برابر کل ({{total}}) باشد",
      errCostPositive: "قیمت باید بیشتر از صفر باشد",
      toastFetchFailed: "بارگذاری خریداری ناموفق بود",
      toastUpdated: "خریداری بروزرسانی شد",
      toastCreated: "خریداری ثبت شد",
      toastSaveFailed: "ذخیره خریداری ناموفق بود",
      toastDeleted: "خریداری موفقانه حذف شد",
      formPurchaseItemsTitle: "اقلام خریداری",
      formAddItem: "افزودن قلم",
      formSelectItem: "انتخاب قلم",
      formUnitCost: "قیمت فی قلم",
      formLineTotal: "مجموع قلم",
      formTotalCostSimple: "مجموع هزینه",
      formPartialPayment: "پرداخت قسمی",
      formFullCredit: "تماماً قرض (بدون پرداخت)",
      formPaymentHintCash: "تمام مبلغ همین حالا پرداخت می‌شود",
      formPaymentHintPartial: "قسمتی حالا پرداخت می‌شود، باقی بعداً",
      formPaymentHintCredit: "فعلاً پرداخت ندارد، بعداً پرداخت شود",
      formPaymentAmount: "مبلغ پرداخت",
      formPaymentAmountPlaceholder: "مبلغ پرداخت فعلی را وارد کنید",
      errAtLeastOneItem: "حداقل یک قلم ضروری است",
      errEachItemValid: "هر قلم باید نام، مقدار بیشتر از صفر و قیمت معتبر داشته باشد",
      errPartialPaymentRequired: "برای پرداخت قسمی، مبلغ پرداخت الزامی است",
      errPartialPaymentLessThanTotal: "پرداخت قسمی باید کمتر از مجموع هزینه باشد",
      printBill: "چاپ بل",
      purchaseBill: "بل خریداری",
      purchaseInformation: "معلومات خریداری",
      billNumber: "نمبر بل",
      forPress: "برای چاپخانه",
      paymentHistory: "تاریخچه پرداخت",
      purchaseNotFound: "خریداری پیدا نشد",
      backToPurchases: "برگشت به خریداری‌ها",
      amount: "مبلغ",
      method: "روش",
      reference: "مرجع"
    },
    items: {
      name: "نام قلم",
      enterName: "نام قلم را وارد کنید",
      nameRequired: "نام قلم الزامی است"
    },
    suppliers: {
      title: "تأمین‌کنندگان",
      addSupplier: "افزودن تأمین‌کننده",
      contact: "تماس",
      searchPlaceholder: "جستجو بر اساس نام، شماره یا ایمیل...",
      noSuppliersFound: "تأمین‌کننده یافت نشد",
      name: "نام",
      phone: "شماره تماس",
      address: "آدرس",
      email: "ایمیل",
      failedToFetch: "بارگذاری تأمین‌کنندگان ناموفق بود",
      failedToDelete: "حذف تأمین‌کننده ناموفق بود",
      editSupplier: "ویرایش تأمین‌کننده",
      addSupplierForm: "افزودن تأمین‌کننده",
      updateInfo: "به‌روزرسانی معلومات تأمین‌کننده",
      createNew: "ایجاد تأمین‌کننده جدید",
      supplierNameRequired: "نام تأمین‌کننده الزامی است",
      enterSupplierName: "نام تأمین‌کننده را وارد کنید",
      contactPerson: "شخص تماس",
      enterContactPerson: "نام شخص تماس را وارد کنید",
      enterPhone: "شماره تماس را وارد کنید",
      enterEmail: "آدرس ایمیل را وارد کنید",
      enterAddress: "آدرس تأمین‌کننده را وارد کنید",
      saving: "در حال ذخیره...",
      updateSupplier: "به‌روزرسانی تأمین‌کننده",
      createSupplier: "ایجاد تأمین‌کننده",
      ledger: "دفتر حساب تأمین‌کننده",
      failedToFetchDetails: "بارگذاری جزئیات تأمین‌کننده ناموفق بود",
      supplierUpdated: "تأمین‌کننده با موفقیت به‌روزرسانی شد",
      supplierCreated: "تأمین‌کننده با موفقیت ایجاد شد",
      failedToSave: "ذخیره تأمین‌کننده ناموفق بود",
      billHistory: "تاریخچه بل",
      openingBalanceBill: "بیلانس اولیه",
      openingBalanceBillDesc: "بیلانس قبلی قبل از سیستم (پرداخت‌ها در پایین)",
      prevBalancePayment: "پرداخت باقی‌مانده قبلی",
      selectBillRequired: "لطفاً حداقل یک بل انتخاب کنید",
      paySpecificBills: "پرداخت بل‌های مشخص",
      payAllDue: "پرداخت همه بدهی‌ها",
      fixedAmount: "پرداخت مبلغ ثابت",
      card: "کارت",
      bankTransfer: "انتقال بانکی",
      check: "چک",
      selectBills: "انتخاب بل‌ها",
      addPreviousBalance: "افزودن باقی‌مانده قبلی",
      editPreviousBalance: "ویرایش باقی‌مانده قبلی",
      deletePrevBalancePayment: "حذف پرداخت باقی‌مانده قبلی",
      confirmDeletePrevBalancePayment: "آیا از حذف این پرداخت باقی‌مانده قبلی مطمئن هستید؟",
      deletePayment: "حذف پرداخت",
      confirmDeletePayment: "آیا از حذف این پرداخت مطمئن هستید؟",
      updatePrevBalancePayment: "مبلغ جدید پرداخت باقی‌مانده قبلی را وارد کنید",
      updatePaymentAmount: "مبلغ جدید پرداخت را وارد کنید",
      prevBalancePaymentUpdated: "پرداخت باقی‌مانده قبلی با موفقیت ویرایش شد",
      prevBalancePaymentUpdateFailed: "ویرایش پرداخت باقی‌مانده قبلی ناموفق بود",
      prevBalancePaymentDeleted: "پرداخت باقی‌مانده قبلی با موفقیت حذف شد",
      prevBalancePaymentDeleteFailed: "حذف پرداخت باقی‌مانده قبلی ناموفق بود"
    },
    employees: {
      title: "کارمندان",
      subtitle: "مدیریت معلومات کارمندان",
      addEmployee: "افزودن کارمند",
      editEmployee: "ویرایش کارمند",
      employeeDetails: "معلومات کارمند",
      searchPlaceholder: "جستجو به‌اساس نام، نمبر تذکره یا شماره تماس...",
      noEmployeesFound: "کارمندی یافت نشد",
      notFound: "کارمند پیدا نشد",
      backToEmployees: "برگشت به لیست کارمندان",
      name: "نام",
      nid: "نمبر تذکره",
      phone: "شماره تماس",
      position: "وظیفه",
      salary: "معاش",
      active: "فعال",
      inactive: "غیرفعال",
      exportPdf: "دانلود PDF",
      monthlySalary: "معاش ماهانه",
      salaryHistory: "تاریخچه معاش",
      salaryEffectiveFrom: "نرخ فعلی از {{date}}",
      balanceOwed: "معاش باقی‌مانده (بدهی به کارمند)",
      pendingAdvances: "پیش‌پیس‌های معلق",
      netSalary: "معاش خالص",
      totalLoans: "مجموع قرض‌ها",
      totalTips: "مجموع انعام‌ها",
      details: "جزئیات",
      advances: "پیش‌پیس‌ها",
      salaryPayments: "پرداخت‌های معاش",
      loans: "قرض‌ها",
      tabMobileDetails: "جزئیات",
      tabMobileAdvances: "پیش‌پیس",
      tabMobileSalary: "معاش",
      tabMobileLoans: "قرض",
      deposit: "سپرده معاش",
      tabMobileDeposit: "سپرده",
      depositBalance: "مبلغی که برای این کارمند نگه داشته می‌شود",
      depositHelp: "وقتی بخشی از معاش را برای نگه‌داری برمی‌گردانند، سپرده ثبت کنید. وقتی پول لازم دارند، از همین بخش پرداخت کنید (بیش از بیلانس نمی‌شود).",
      depositHoldTitle: "کارمند سپرده کرد (نگه‌داری در شرکت)",
      depositPayoutTitle: "پرداخت به کارمند",
      recordHold: "ثبت سپرده",
      recordPayout: "پرداخت از سپرده",
      entryTypeHold: "سپرده",
      entryTypePayout: "پرداخت‌شده",
      noDepositEntries: "هنوز فعالیتی برای سپرده نیست",
      depositAmountRequired: "مبلغ معتبر بزرگ‌تر از صفر وارد کنید.",
      depositHoldSaved: "سپرده ثبت شد.",
      depositPayoutSaved: "پرداخت ثبت شد.",
      depositSaveFailed: "ثبت سپرده ناموفق بود.",
      depositEntryUpdated: "سپرده به‌روز شد.",
      depositEditTitle: "ویرایش سپرده",
      printDepositSlip: "چاپ رسید",
      personalInformation: "معلومات شخصی",
      fatherName: "نام پدر",
      employmentInformation: "معلومات کاری",
      joinDate: "تاریخ شمولیت به کار",
      fromToToday: "از {{date}} تا امروز",
      totalTipsReceived: "انعام‌های دریافت‌شده",
      addTip: "ثبت انعام",
      advancesAutoDeducted: "پیش‌پیس‌ها هنگام پرداخت معاش به‌صورت خودکار از معاش کسر می‌شود.",
      addAdvance: "ثبت پیش‌پیس",
      amount: "مبلغ",
      amountAfnRequired: "مبلغ (افغانی) *",
      deductionDate: "تاریخ کسر",
      returnPlan: "طرح بازپرداخت پیش‌پیس",
      deducted: "کسر شده",
      pending: "معلق",
      noAdvancesRecorded: "هیچ پیش‌پیسی ثبت نشده است",
      unpaidMonths: "{{count}} ماه پرداخت‌نشده — بیلانس: {{balance}}",
      paySalary: "پرداخت معاش",
      month: "ماه",
      baseSalary: "معاش پایه",
      advanceDeducted: "پیش‌پیس کسرشده",
      netPaid: "خالص پرداخت‌شده",
      paymentDate: "تاریخ پرداخت",
      notes: "یادداشت",
      noSalaryPaymentsRecorded: "هیچ پرداخت معاش ثبت نشده است",
      loansNoteTitle: "توجه:",
      loansNote: "قرض‌ها جدا از پیش‌پیس است و از معاش ماهانه کم نمی‌شود؛ جداگانه پیگیری و ادا شود.",
      addLoan: "ثبت قرض",
      paid: "پرداخت‌شده",
      remaining: "باقیمانده",
      interestRate: "فیصدی سود",
      repaymentPlan: "طرح ادا کردن قرض",
      noLoansRecorded: "هیچ قرضی ثبت نشده است",
      loanStatusPaid: "تسویه‌شده",
      loanStatusPartial: "قسمی",
      loanStatusActive: "جاری",
      editAdvance: "ویرایش پیش‌پیس",
      dateGiven: "تاریخ دریافت پیش‌پیس",
      dateRequired: "تاریخ *",
      returnPlanNote: "(یادآوری: پیش‌پیس با پرداخت معاش خودکار کسر می‌شود.)",
      returnPlanPlaceholder: "مثلاً: از معاش ماه بعد کسر می‌شود",
      additionalNotes: "یادداشت اضافی",
      notesOptionalPlaceholder: "یادداشت اضافی (اختیاری)",
      createAdvance: "ثبت پیش‌پیس",
      updateAdvance: "بروزرسانی پیش‌پیس",
      recordSalaryPayment: "ثبت پرداخت معاش",
      salaryType: "نوع معاش",
      monthly: "ماهانه",
      weekly: "هفتگی",
      weeklySalaryApprox: "معاش هفتگی (تقریبی)",
      weekStartDate: "آغاز هفته *",
      weeklyPaidDateLabel: "تاریخ پرداخت *",
      weeklyPaidDateHelp:
        "روزی که برای این هفته معاش می‌پردازید (در دری/پشتو تقویم شمسی نمایش داده می‌شود و همان‌طور ذخیره می‌شود).",
      paymentDateGregorianHint: "میلادی",
      payAllDueBalance: "پرداخت همهٔ معاش معوق ({{count}} ماه)",
      totalGross: "مجموع ناخالص: {{amount}}",
      pendingAdvancesWillDeduct: "- پیش‌پیس معلق: {{amount}}",
      netAmount: "= خالص: {{amount}}",
      selectMonthsToPay: "ماه‌ها را برای پرداخت انتخاب کنید *",
      noUnpaidMonthsFound: "ماه پرداخت‌نشده‌ای نیست",
      selectedMonths: "انتخاب‌شده: {{count}} ماه",
      grossAmount: "ناخالص: {{amount}}",
      pendingAdvancesInfo: "پیش‌پیس معلق به مبلغ {{amount}} از پرداخت معاش خودکار کسر می‌شود.",
      monthlySalaryLabel: "معاش ماهانه:",
      weeklyApproxLabel: "هفتگی (تقریبی):",
      payWeeklySalary: "پرداخت معاش هفتگی",
      payAllMonths: "پرداخت همه ({{count}} ماه)",
      paySelectedMonths: "پرداخت انتخاب‌شده ({{count}} ماه)",
      selectMonthsToPayButton: "ماه را انتخاب کنید",
      editLoan: "ویرایش قرض",
      loanAmount: "مبلغ قرض (افغانی) *",
      loanPrincipalLabel: "مبلغ قرض",
      loanDate: "تاریخ قرض *",
      interestRatePercent: "فیصدی سود (٪)",
      howWillLoanBeRepaid: "این قرض چگونه ادا می‌شود؟",
      createLoan: "ثبت قرض",
      updateLoan: "بروزرسانی قرض",
      recordLoanPayment: "ثبت پرداخت قرض",
      alreadyPaid: "پیش‌تر پرداخت‌شده",
      remainingAmount: "مبلغ باقیمانده",
      paymentAmount: "مبلغ پرداخت (افغانی) *",
      recordPayment: "ثبت پرداخت",
      addTipTitle: "ثبت انعام",
      tipAmount: "مبلغ (افغانی) *",
      reason: "علت / توضیح",
      reasonPlaceholder: "مثلاً: کار خوب، اضافه‌کاری",
      deleteAdvance: "حذف پیش‌پیس",
      deleteLoan: "حذف قرض",
      deleteConfirmMessage: "آیا از حذف این {{type}} مطمئن هستید؟ این کار برگشت‌ناپذیر است.",
      advanceNoun: "پیش‌پیس",
      loanNoun: "قرض",
      editSalaryPayment: "ویرایش پرداخت معاش",
      updatePayment: "بروزرسانی پرداخت",
      employeeIdLabel: "شماره",
      editPaymentTitle: "ویرایش پرداخت",
      deletePaymentTitle: "حذف پرداخت",
      recordPaymentActionTitle: "ثبت پرداخت",
      printReportTitle: "راپور معلومات کارمند",
      printDocumentTitle: "کارمند — {{name}}",
      financialSummary: "خلاصه مالی",
      employeeInformation: "معلومات کارمند",
      metricCol: "شرح",
      amountAfnCol: "مبلغ (افغانی)",
      totalAdvancesLabel: "مجموع پیش‌پیس‌ها",
      totalSalaryPaidLabel: "مجموع معاش پرداخت‌شده",
      totalAdvancesDeductedLabel: "مجموع پیش‌پیس کسرشده",
      reportDate: "تاریخ راپور",
      weeklySalaryNotesDefault: "پرداخت معاش هفتگی",
      bulkPaymentMonthsNote: "پرداخت گروپ برای {{count}} ماه",
      addEmployeePageTitle: "افزودن کارمند",
      editEmployeePageTitle: "ویرایش کارمند",
      addEmployeePageSubtitle: "ایجاد کارمند جدید",
      editEmployeePageSubtitle: "به‌روزرسانی معلومات کارمند",
      enterName: "نام کارمند را وارد نمایید",
      enterFatherName: "نام پدر را وارد نمایید",
      enterNid: "نمبر تذکره را وارد نمایید",
      enterPhone: "نمبر تلفن را وارد نمایید",
      enterSalary: "مقدار معاش را وارد نمایید",
      enterAddress: "آدرس کارمند را وارد نمایید",
      fatherName: "نام پدر",
      nid: "نمبر تذکره",
      salary: "معاش",
      joinDate: "تاریخ شمولیت به کار",
      activeEmployee: "کارمند فعال",
      updateEmployee: "بروزرسانی کارمند",
      createEmployee: "ایجاد کارمند",
      nameRequired: "نام الزامی است",
      fatherNameRequired: "نام پدر الزامی است",
      nidRequired: "نمبر تذکره الزامی است",
      phoneRequired: "شماره تماس الزامی است",
      addressRequired: "آدرس الزامی است",
      salaryRequired: "لطفاً معاش معتبر وارد کنید",
      joinDateRequired: "تاریخ شمولیت الزامی است",
      failedToSave: "ذخیره معلومات کارمند ناموفق بود. لطفاً دوباره تلاش کنید.",
      failedToFetch: "بارگذاری کارمندان ناموفق بود",
      failedToDelete: "حذف کارمند ناموفق بود",
      status: "حالت",
      toast: {
        fetchFailed: "بارگذاری معلومات کارمند ناموفق بود",
        advanceCreated: "پیش‌پیس با موفقیت ثبت شد",
        advanceCreateFailed: "ثبت پیش‌پیس ناموفق بود",
        advanceUpdated: "پیش‌پیس بروزرسانی شد",
        advanceUpdateFailed: "بروزرسانی پیش‌پیس ناموفق بود",
        advanceDeleted: "پیش‌پیس حذف شد",
        loanDeleted: "قرض حذف شد",
        deleteFailed: "حذف ناموفق بود",
        loanCreated: "قرض ثبت شد",
        loanCreateFailed: "ثبت قرض ناموفق بود",
        loanUpdated: "قرض بروزرسانی شد",
        loanUpdateFailed: "بروزرسانی قرض ناموفق بود",
        loanDeletedSingle: "قرض حذف شد",
        loanDeleteFailed: "حذف قرض ناموفق بود",
        tipCreated: "انعام ثبت شد",
        tipCreateFailed: "ثبت انعام ناموفق بود",
        weeklySalaryRecorded: "پرداخت معاش هفتگی ثبت شد",
        selectMonthError: "حداقل یک ماه را برای پرداخت انتخاب کنید",
        noMonthsSelected: "هیچ ماهی برای پرداخت انتخاب نشده است",
        salaryRecordedMonths: "پرداخت معاش برای {{count}} ماه با موفقیت ثبت شد",
        salaryCreateFailed: "ثبت پرداخت معاش ناموفق بود",
        invalidSalaryAmount: "لطفاً مبلغ معاش درست وارد کنید",
        salaryUpdated: "پرداخت معاش بروزرسانی شد",
        salaryUpdateFailed: "بروزرسانی پرداخت معاش ناموفق بود",
        salaryPaymentDateRequired: "لطفاً تاریخ پرداخت را انتخاب کنید",
        salaryDeleted: "پرداخت معاش حذف شد",
        salaryDeleteFailed: "حذف پرداخت معاش ناموفق بود",
        loanPaymentRecorded: "پرداخت قرض ثبت شد",
        loanPaymentFailed: "ثبت پرداخت قرض ناموفق بود",
        pdfTableWarning: "PDF بدون جدول کامل ساخته شد؛ صفحه را تازه کنید.",
        pdfExportFailed: "خروجی PDF ناموفق: {{message}}"
      }
    },
    expenses: {
      title: "مصارفات",
      addExpense: "افزودن مصارفات",
      addExpenseForm: "افزودن مصارفات",
      searchPlaceholder: "جستجو توضیحات یا یادداشت...",
      allCategories: "همه دسته‌ها",
      officeSupplies: "لوازم اداری",
      utilities: "آب و برق",
      transport: "حمل و نقل",
      marketing: "تبلیغات",
      maintenance: "تعمیر و نگهداری",
      other: "سایر",
      fromDate: "از تاریخ",
      toDate: "تا تاریخ",
      total: "مجموع",
      dailyTotal: "مجموع روز",
      description: "توضیحات",
      category: "دسته",
      amount: "مبلغ",
      date: "تاریخ",
      actions: "اقدامات",
      deleteExpense: "حذف مصارفات",
      deleteConfirm: "آیا مطمئن هستید که می‌خواهید این مصارفات را حذف کنید؟ این کار قابل بازگشت نیست.",
      failedToFetch: "بارگذاری مصارفات ناموفق بود",
      failedToDelete: "حذف مصارفات ناموفق بود",
      deletedSuccess: "مصارفات با موفقیت حذف شد",
      homeExpensesCard: "مصارفات خانه",
      pressExpensesCard: "مصارفات چاپخانه",
      periodFilterHint: "فیلتر بر اساس دوره (هر دو کارت و جدول)",
      tableFilterLabel: "جدول:",
      showAllLocations: "همه",
      filterHomeOnly: "فقط خانه",
      filterPressOnly: "فقط چاپخانه",
      entriesInPeriod: "{{count}} مورد",
      printHomeReport: "چاپ مصارف خانه",
      printPressReport: "چاپ مصارف چاپخانه",
      printWindowTitle: "مصارفات",
      minAmount: "حداقل مبلغ",
      maxAmount: "حداکثر مبلغ",
      location: "محل",
      locationHome: "خانه",
      locationPress: "چاپخانه",
      enterCategoryName: "نام کتگوری را وارد کنید",
      addNewCategory: "افزودن کتگوری جدید",
      amountPlaceholder: "۰.۰۰",
      categories: {
        office: "لوازم دفتر",
        utilities: "خدمات",
        transport: "حمل و نقل",
        marketing: "بازاریابی",
        maintenance: "تعمیر و نگهداری",
        other: "سایر"
      },
      editExpense: "ویرایش مصارفات",
      addExpenseForm: "افزودن مصارفات",
      updateInfo: "به‌روزرسانی معلومات مصارفات",
      createNew: "ایجاد مصارفات جدید",
      descriptionRequired: "توضیحات الزامی است",
      amountRequired: "مبلغ باید بزرگتر از صفر باشد",
      enterDescription: "توضیحات مصارفات را وارد کنید",
      enterAmount: "مبلغ را وارد کنید",
      selectCategory: "دسته را انتخاب کنید",
      selectDate: "تاریخ را انتخاب کنید",
      enterNotes: "یادداشت اضافی...",
      saving: "در حال ذخیره...",
      updateExpense: "به‌روزرسانی مصارفات",
      saveExpense: "ذخیره مصارفات",
      failedToFetchExpense: "بارگذاری مصارفات ناموفق بود",
      expenseUpdated: "مصارفات با موفقیت به‌روزرسانی شد",
      expenseCreated: "مصارفات با موفقیت ایجاد شد",
      failedToSaveExpense: "ذخیره مصارفات ناموفق بود"
    },
    reportsPage: {
      title: "گزارشات و تحلیل",
      subtitle: "بینش و تحلیل جامع تجاری",
      refresh: "تازه‌سازی",
      detailed: "گزارش تفصیلی",
      ordersWithDues: "سفارشات با بدهی",
      suppliersBalance: "موجودی تأمین‌کنندگان",
      lowStock: "موجودی کم",
      pendingAdvances: "پیش‌پرداخت‌های معوق",
      monthlyExpenses: "مصارفات ماهانه",
      employeeSalary: "حقوق کارمندان",
      purchasePayments: "پرداخت‌های خریداری",
      roznamcha: "روزنامه",
      failedToFetch: "بارگذاری ناموفق بود",
      noData: "داده وجود ندارد",
      filters: "فیلترها",
      period: "دوره",
      month: "ماه",
      daily: "روزانه",
      weekly: "هفته وار",
      monthly: "ماهانه",
      yearly: "سالانه",
      customRange: "محدوده سفارشی",
      startDate: "تاریخ شروع",
      endDate: "تاریخ پایان",
      applyFilters: "اعمال فیلترها",
      exportPdf: "PDF",
      exportExcel: "Excel",
      exportCsv: "CSV",
      exportToPdf: "صادرات به PDF",
      exportToExcel: "صادرات به Excel",
      exportToCsv: "صادرات به CSV",
      pdfExported: "PDF با موفقیت صادر شد",
      excelExported: "فایل Excel با موفقیت صادر شد",
      csvExported: "CSV با موفقیت صادر شد",
      exportFailed: "صادرات ناموفق بود",
      noDataForExport: "داده‌ای برای صادرات موجود نیست",
      generated: "تولید شده",
      revenue: "درآمد",
      costs: "هزینه‌ها",
      expenses: "مصارفات",
      profit: "سود",
      receivables: "دریافتنی‌ها",
      payables: "پرداختنی‌ها",
      financialSummary: "خلاصه مالی",
      ordersSummary: "خلاصه سفارشات",
      purchasesSummary: "خلاصه خریداری‌ها",
      expensesSummary: "خلاصه مصارفات",
      inventorySummary: "خلاصه موجودی",
      totalOrders: "مجموع سفارشات",
      totalRevenue: "مجموع درآمد",
      totalDue: "مجموع بدهی",
      totalPurchases: "مجموع خریداری‌ها",
      totalCost: "مجموع هزینه",
      remaining: "باقیمانده",
      totalExpenses: "مجموع مصارفات",
      count: "تعداد",
      totalItems: "مجموع اقلام",
      stockValue: "ارزش موجودی",
      lowStockItems: "اقلام موجودی کم",
      financialOverview: "نمای کلی مالی",
      ordersByStatus: "سفارشات بر اساس وضعیت",
      expensesByCategory: "مصارفات بر اساس دسته",
      directSalesSection: "فروش‌های مستقیم (بدون موجودی)",
      directSalesCount: "تعداد فروش‌های مستقیم",
      directSalesRevenue: "درآمد فروش‌های مستقیم",
      directSalesCost: "هزینه فروش‌های مستقیم",
      directSalesProfit: "سود فروش‌های مستقیم",
      directSalesDetails: "جزئیات فروش‌های مستقیم",
      saleId: "شناسه فروش",
      totalOutstanding: "مجموع معوق",
      ordersWithOutstandingDues: "سفارش(ها) با بدهی معوق",
      orderId: "شناسه سفارش",
      customer: "مشتری",
      phone: "شماره تماس",
      total: "مجموع",
      paid: "پرداخت شده",
      due: "بدهی",
      date: "تاریخ",
      status: "وضعیت",
      totalOutstandingBalance: "مجموع موجودی معوق",
      suppliersWithOutstandingBalance: "تأمین‌کننده(ها) با موجودی معوق",
      supplierId: "شناسه تأمین‌کننده",
      name: "نام",
      contact: "تماس",
      balance: "موجودی",
      created: "ایجاد شده",
      itemsBelowMinimum: "اقلام زیر حداقل سطح موجودی",
      itemId: "شناسه قلم",
      sku: "کد محصول",
      type: "نوع",
      currentStock: "موجودی فعلی",
      minimum: "حداقل",
      unitPrice: "قیمت واحد",
      stockValueItem: "ارزش موجودی",
      totalPendingAdvances: "مجموع پیش‌پرداخت‌های معوق",
      pendingAdvance: "پیش‌پرداخت معوق",
      advanceId: "شناسه پیش‌پرداخت",
      employee: "کارمند",
      amount: "مبلغ",
      dateGiven: "تاریخ داده شده",
      returnPlan: "طرح بازگشت",
      notes: "یادداشت‌ها",
      totalMonthlyExpenses: "مجموع مصارفات ماهانه",
      periodLabel: "دوره",
      expense: "مصارفات",
      activeEmployees: "کارمندان فعال",
      employeeId: "شناسه کارمند",
      salary: "حقوق",
      pendingAdvances: "پیش‌پرداخت‌های معوق",
      netSalary: "حقوق خالص",
      joinDate: "تاریخ پیوستن",
      purchasePaymentStatus: "وضعیت پرداخت خریداری",
      totalPurchasesLabel: "مجموع خریداری‌ها",
      paymentStatusSummary: "خلاصه وضعیت پرداخت",
      purchaseId: "شناسه خریداری",
      supplier: "تأمین‌کننده",
      item: "قلم",
      quantity: "تعداد",
      cost: "هزینه",
      purchaseDetails: "جزئیات خریداری",
      roznamchaReport: "گزارش روزنامه",
      entries: "ورودی‌ها",
      itemName: "نام قلم",
      description: "توضیحات",
      costPrice: "هزینه/قیمت",
      minDue: "حداقل بدهی",
      maxDue: "حداکثر بدهی",
      minAmount: "حداقل مبلغ",
      maxAmount: "حداکثر مبلغ",
      itemType: "نوع قلم",
      allTypes: "همه انواع",
      rawMaterial: "مواد خام",
      finishedProduct: "محصول نهایی",
      criticalOnly: "فقط بحرانی",
      page: "صفحه",
      of: "از",
      noDataAvailable: "داده موجود نیست. برای تولید گزارش روی 'اعمال فیلترها' کلیک کنید.",
      category: "دسته",
      totalAmount: "مجموع مبلغ",
      printingSummary: "خلاصه چاپخانه",
      totalPrintingCost: "مجموع مصرف چاپ",
      printingDetails: "جزئیات چاپخانه",
      paymentStatus: "وضعیت پرداخت",
      netAmount: "مبلغ خالص"
    },
    recordLookup: {
      title: "جستجوی سوابق",
      subtitle: "جستجو با نام، شناسه یا شماره تماس مشتری/تأمین‌کننده",
      placeholder: "نام، شناسه یا شماره تماس را وارد کنید",
      enterQuery: "لطفاً متن جستجو را وارد کنید",
      searchFailed: "جستجوی سوابق ناکام شد",
      matchedCustomers: "مشتریان مطابق",
      matchedSuppliers: "تأمین‌کنندگان مطابق",
      orders: "سفارشات",
      sales: "فروش",
      directSales: "فروش مستقیم",
      quotations: "کوټیشن",
      purchases: "خریداری"
    },
    sidebar: {
      switchToDari: "دری",
      switchToEnglish: "انگلیسی"
    }
  },
  ps: {
    common: {
      all: "ټول",
      loading: "بار کېږي...",
      date: "نېټه",
      items: "توکي",
      cost: "لګښت",
      back: "شاته",
      prev: "مخکینی",
      next: "راتلونکی",
      notes: "یادداشتونه",
      notesPlaceholder: "اضافي یادداشتونه (اختیاري)",
      view: "کتل",
      edit: "سمول",
      cancel: "لغوه کول",
      save: "ساتل",
      actions: "کړنې"
    },
    nav: {
      bank: "بانک"
    },
    pagination: {
      showing: "ښکاره",
      to: "تر",
      of: "له",
      page: "پاڼه",
      previous: "مخکینی",
      next: "راتلونکی"
    },
    sales: {
      financial: "مالي معلومات",
      directSalesTitle: "مستقیم پلور",
      directSalesSubtitle: "توکي بهر څخه اخیستل شوي او مستقیم پیرودونکي ته پلورل شوي",
      newDirectSale: "نوی مستقیم پلور",
      noDirectSalesFound: "هیڅ مستقیم پلور ونه موندل شو",
      createFirstDirectSale: "لومړی مستقیم پلور جوړ کړئ",
      unpaid: "نادفع",
      paid: "ورکړل شوی",
      partial: "برخه",
      totalAmount: "ټوله پیسې",
      profit: "ګټه",

      createDirectSaleTitle: "مستقیم پلور جوړول",
      createDirectSaleSubtitle: "له بهر نه واخلئ او بې له ګدامه سم پیرودونکي ته وپلورئ",
      customerInformation: "د پیرودونکي معلومات",
      customerNameRequiredLabel: "د پیرودونکي نوم *",
      selectCustomerOptional: "له لړۍ څخه ټاکل (اختیاري)",
      selectCustomerPlaceholder: "پیرودونکی وټاکئ...",
      itemsToSell: "د پلور توکي",
      sellingPricePerUnit: "د پلور بیه *",
      costPricePerUnit: "د پېرود بیه *",
      supplierNameOptionalLabel: "عرضه کوونکی / منبع (اختیاري)",
      financialSummary: "مالي لنډیز",
      subtotalSelling: "منځټولې (د پلور):",
      totalCostBuying: "ټول لګښت:",
      createDirectSaleDraft: "مستقیم پلور (مسوده) جوړ کړئ",
      confirmDirectSale: "تایید او بیل جوړ کړئ",
      failedToCreateDirectSale: "مستقیم پلور جوړ نشو",
      directSaleCreated: "مستقیم پلور په بریالیتوب سره جوړ شو",
      directSaleConfirmed: "مستقیم پلور په بریالیتوب سره تأیید شو"
    },
    bank: {
      title: "بانک",
      subtitle: "د پیسو زېرمه او ایستل مدیریت کړئ",
      currentBalance: "اوسنی بیلانس",
      totalDeposits: "ټولې زېرمي",
      totalWithdraws: "ټولې ایستنې",
      availableIncome: "د زېرمه کولو لپاره شته عاید",
      newTransaction: "نوې راکړه ورکړه",
      depositIncome: "عاید زېرمه کړئ",
      deposit: "زېرمه",
      withdraw: "ایستل",
      credit: "کریډیټ",
      debit: "ډیبیټ",
      manual: "لاسي",
      income: "عاید",
      amount: "مقدار",
      type: "ډول",
      source: "سرچینه",
      note: "یادښت",
      transactions: "راکړې ورکړې",
      noTransactions: "هیڅ راکړه ورکړه نشته",
      failedToLoad: "د بانک معلومات پورته نه شول",
      amountRequired: "مقدار باید له صفر زیات وي",
      failedToSave: "راکړه ورکړه ونه ساتل شوه",
      depositSuccess: "زېرمه په بریالیتوب ثبت شوه",
      withdrawSuccess: "ایستل په بریالیتوب ثبت شول",
      updateSuccess: "راکړه ورکړه په بریالیتوب نوي شوه",
      deleteSuccess: "راکړه ورکړه په بریالیتوب حذف شوه",
      failedToDelete: "راکړه ورکړه حذف نه شوه",
      incomeDepositSuccess: "عاید په بریالیتوب زېرمه شو",
      noIncomeAvailable: "د زېرمه کولو لپاره عاید نشته"
    },
    customers: {
      manualSerialNo: "سیریال نمبر",
      manualSerialNoPlaceholder: "د دفتر / مرجع نمبر (اختیاري)",
      searchPlaceholderSerial: "په نوم، تلیفون یا سیریال لټون...",
      hasDue: "بقیه لري",
      previousBalance: "مخکینی بقیه",
      previousBalanceRemaining: "پاتې بقیه",
      noPreviousBalance: "د دې پیرودونکي لپاره هیڅ مخکینی بقیه نشته.",
      enterValidAmount: "مهرباني وکړئ یو سم مقدار ولیکئ.",
      previousBalancePaid: "د مخکیني بقیې پیسې په بریالیتوب سره ثبت شوې.",
      amountPlaceholder: "مقدار",
      payingPreviousBalance: "په تادیه بوخت...",
      payPreviousBalance: "مخکینی بقیه تادیه کړئ"
    }
  }
};


let currentLang = localStorage.getItem('i18nextLng') || 'en';

export const getTranslation = (key, params = {}) => {
  const keys = key.split('.');
  let value = translations[currentLang];
  for (const k of keys) {
    value = value?.[k];
  }
  if (!value || value === key) {
    if (currentLang !== 'en') {
      value = translations.en;
      for (const k of keys) {
        value = value?.[k];
      }
    }
  }
  if (!value || value === key) return key;
  
  // Simple parameter replacement
  if (params && typeof value === 'string') {
    return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
      return params[paramKey] !== undefined ? params[paramKey] : match;
    });
  }
  
  return value || key;
};

const I18N_CHANGE_EVENT = 'i18n:languageChange';

export const setLanguage = (lang) => {
  currentLang = lang;
  localStorage.setItem('i18nextLng', lang);
  document.documentElement.setAttribute('dir', (lang === 'prs') ? 'rtl' : 'ltr');
  document.documentElement.setAttribute('lang', lang);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(I18N_CHANGE_EVENT, { detail: { language: lang } }));
  }
};

// Initialize
setLanguage(currentLang);

// Simple hook-like function - subscribes to language changes so UI updates without refresh
export const useTranslation = () => {
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const handler = () => setTick((n) => n + 1);
    window.addEventListener(I18N_CHANGE_EVENT, handler);
    return () => window.removeEventListener(I18N_CHANGE_EVENT, handler);
  }, []);
  // Stable reference: getTranslation reads currentLang at call time; language switches still
  // re-render via setTick. A new `t` every render breaks useCallback/useEffect deps (e.g. Dashboard fetch loop).
  const t = React.useCallback((key, params) => getTranslation(key, params), []);
  return {
    t,
    formatDate: formatDateUtil,
    formatTime: formatTimeUtil,
    formatDateLong: formatDateLongUtil,
    i18n: {
      language: currentLang,
      changeLanguage: setLanguage
    }
  };
};

// Default export for config.js compatibility
export default {
  get currentLang() { return currentLang; },
  getTranslation,
  setLanguage
};
